import type { SxProps, Theme } from "@mui/material/styles";
import { BUSINESS_THEME, IS_CLINIC, LABELS, SHOW } from "../platform/config";
import { ExportProgressReporter } from "../components/CustomButton";
import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Button,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from "@mui/icons-material/Add";
import CustomLabel from "../components/CustomLabel";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import CustomTable from "../components/table/CustomTable";
import CustomTextField from "../components/CustomTextField";
import DraggableMap from "../components/map/DraggableMap";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import CustomDialog from "../components/CustomDialog";
import CustomButton from "../components/CustomButton";
import { COLORS } from "../assets/theme";
import {
  getAllBranches,
  createBranch,
  getAllBusinessTypes,
} from "../services/BranchesService";
import { countries } from "../assets/utils/countries";
import { findServiceDuration } from "../utils/helpers";
import { getAllServices } from "../services/ServicesService";
import {
  updateAppointment,
  deleteAppointment,
} from "../services/AppointmentService";

import CustomSelect from "../components/CustomSelect";
import {
  getDashboardAppointments,
  getDashboardRevenueSummary,
} from "../services/newDashboardService";
import PaidIcon from "@mui/icons-material/Paid";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import {
  clearCachedPinAccess,
  getCachedPinAccess,
  subscribeToPinAccess,
} from "../utils/pinAccessCache";
import PinAccessDialog from "../components/PinAccessDialog";
import { getClientsPage, getClientsSelect } from "../services/ClientsService";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import InvoiceItemsEditor, {
  InvoiceItem,
  isInvoiceProductMissingSelection,
} from "../components/invoices/InvoiceItemsEditor";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  getInvoiceById,
  updateQuotation,
  payInvoice,
  refundInvoice,
} from "../services/newInvoicesService";
import { ADMIN_APPOINTMENT_SOCKET_EVENT } from "../services/adminAppointmentSocket";
import { useCountUpText } from "../hooks/useCountUpText";

// Runs the count-up in its own component so each animation frame re-renders
// only this label, not the whole dashboard (and its table) with it.
const CountUpLabel = ({
  text,
  ...rest
}: React.ComponentProps<typeof CustomLabel>) => (
  <CustomLabel text={useCountUpText(text)} {...rest} />
);

const showErrorToast = (message: any, opts: any = {}) =>
  toast.error(String(message), { toastId: String(message), ...opts });

const FALLBACK_SERVICE_OPTIONS = [60, 90, 120];

const getUnitOrderValue = (unit: any): number | null => {
  const rawOrder =
    unit?.order ??
    unit?.orderNumber ??
    unit?.order_number ??
    unit?.orderNo ??
    unit?.order_no;
  if (rawOrder === "" || rawOrder === undefined || rawOrder === null)
    return null;
  const parsed = Number(rawOrder);
  return Number.isFinite(parsed) ? parsed : null;
};

const sortUnitsByOrder = <T extends { name?: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => {
    const aOrder = getUnitOrderValue(a);
    const bOrder = getUnitOrderValue(b);
    if (aOrder !== null && bOrder !== null) return aOrder - bOrder;
    if (aOrder !== null) return -1;
    if (bOrder !== null) return 1;
    const aName = (a?.name ?? "").toString();
    const bName = (b?.name ?? "").toString();
    return aName.localeCompare(bName);
  });
};

const buildOrderedUnitNames = (items: { name?: string }[]): string[] => {
  const seen = new Set<string>();
  return sortUnitsByOrder(items)
    .map((unit) => (unit?.name ? String(unit.name) : ""))
    .filter((name) => {
      const trimmed = name.trim();
      if (!trimmed || seen.has(trimmed)) return false;
      seen.add(trimmed);
      return true;
    });
};

const formTimeStart = "00:00";
const MAX_SERVICE_DURATION_MIN = 500;

const parseTimeToMinutes = (time: string): number => {
  if (!time) return 0;
  let t = String(time).trim().toLowerCase();
  const isPM = t.includes("pm");
  const isAM = t.includes("am");
  t = t.replace(/am|pm/g, "").trim();
  const [hRaw = "0", mRaw = "0"] = t.split(":");
  let h = parseInt(hRaw, 10);
  let m = parseInt(mRaw, 10);
  if (!Number.isFinite(h)) h = 0;
  if (!Number.isFinite(m)) m = 0;
  if (isPM && h < 12) h += 12;
  if (isAM && h === 12) h = 0;
  if (!isAM && !isPM && h === 24) h = 0;
  return h * 60 + m;
};

const formatMinutesTo12h = (totalMinutes: number): string => {
  const mins = ((totalMinutes % 1440) + 1440) % 1440; // keep within a day
  let hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  const suffix = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

// Returns the first non-empty string/number, used by invoice normalization.
const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
    if (typeof value === "number") {
      const text = String(value);
      if (text) return text;
    }
  }
  return undefined;
};

const buildDateWithTime = (
  dateStr?: string | null,
  timeStr?: string | null
): Date | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;

  const minutes = parseTimeToMinutes(String(timeStr || "00:00"));
  const addDays = Math.floor(minutes / 1440);
  const minsInDay = ((minutes % 1440) + 1440) % 1440;
  const hours = Math.floor(minsInDay / 60);
  const mins = minsInDay % 60;

  date.setHours(hours, mins, 0, 0);
  if (addDays > 0) {
    date.setDate(date.getDate() + addDays);
  }
  return date;
};

type AddressAutocompleteProps = {
  value: string | undefined | null;
  onChange: (v: any) => void;
  helpers?: {
    setFieldValue: (name: string, value: any) => void;
    values?: Record<string, any>;
  };
};

interface Service {
  id: string;
  _id?: string;
  name?: string;
  title?: string;
  duration?: number | null;
  price?: string | number;
  serviceprice?: string | number;
  servicePrice?: string | number;
  price_discount?: string | number;
  service_id?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  helpers,
}) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const googleReady = useRef<boolean>(false);
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;

    if (
      (window as any).google &&
      (window as any).google.maps &&
      (window as any).google.maps.places
    ) {
      googleReady.current = true;
      return;
    }

    const scriptId = "google-maps-js";
    if (document.getElementById(scriptId)) {
      const check = setInterval(() => {
        if (
          (window as any).google &&
          (window as any).google.maps &&
          (window as any).google.maps.places
        ) {
          googleReady.current = true;
          clearInterval(check);
        }
      }, 200);
      return () => clearInterval(check);
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&language=en`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleReady.current = true;
    };
    script.onerror = () => {
      googleReady.current = false;
    };
    document.head.appendChild(script);

    return () => {};
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const triggerGeocode = (v: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const q = String(v || "").trim();
        if (q.length < 2) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }

        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (key && googleReady.current && (window as any).google) {
          try {
            setLoadingSuggest(true);
            const service = new (
              window as any
            ).google.maps.places.AutocompleteService();
            service.getPlacePredictions(
              {
                input: q,
                componentRestrictions: { country: "lb" },
                language: "en",
              },
              (preds: any[], status: any) => {
                setLoadingSuggest(false);
                if (
                  status ===
                    (window as any).google.maps.places.PlacesServiceStatus.OK &&
                  Array.isArray(preds)
                ) {
                  setSuggestions(preds);
                  setShowSuggestions(true);
                  if (preds[0] && preds[0].place_id) {
                    const ps = new (
                      window as any
                    ).google.maps.places.PlacesService(
                      document.createElement("div"),
                    );
                    ps.getDetails(
                      { placeId: preds[0].place_id },
                      (place: any) => {
                        if (
                          place &&
                          place.geometry &&
                          place.geometry.location
                        ) {
                          const lat = place.geometry.location.lat();
                          const lng = place.geometry.location.lng();
                          helpers?.setFieldValue("latitude", String(lat));
                          helpers?.setFieldValue("longitude", String(lng));
                        }
                      },
                    );
                  }
                } else {
                  setSuggestions([]);
                  setShowSuggestions(false);
                }
              },
            );
          } catch (err) {
            setLoadingSuggest(false);
          }
          return;
        }

        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=lb&limit=10&accept-language=en&q=${encodeURIComponent(
          q,
        )}`;
        setLoadingSuggest(true);
        const resp = await fetch(url, {
          headers: {
            Accept: "application/json",
            "Accept-Language": "en",
            "User-Agent": "PadelFrontendDash/1.0",
          },
        });
        if (!resp.ok) {
          setLoadingSuggest(false);
          return;
        }
        const data = await resp.json();
        if (Array.isArray(data) && data.length > 0) {
          setSuggestions(data);
          setShowSuggestions(true);
          const { lat, lon } = data[0];
          helpers?.setFieldValue("latitude", String(lat));
          helpers?.setFieldValue("longitude", String(lon));
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
      } finally {
        setLoadingSuggest(false);
      }
    }, 250) as unknown as number;
  };

  const latNum = helpers?.values?.latitude
    ? Number(helpers?.values?.latitude)
    : undefined;
  const lngNum = helpers?.values?.longitude
    ? Number(helpers?.values?.longitude)
    : undefined;

  const handleSelectSuggestion = (s: any) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (key && googleReady.current && s.place_id && (window as any).google) {
      const ps = new (window as any).google.maps.places.PlacesService(
        document.createElement("div"),
      );
      ps.getDetails({ placeId: s.place_id }, (place: any, status: any) => {
        if (
          status ===
            (window as any).google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const formatted =
            place.formatted_address ||
            place.name ||
            s.description ||
            s.display_name;
          onChange(formatted);
          if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            helpers?.setFieldValue("latitude", String(lat));
            helpers?.setFieldValue("longitude", String(lng));
          }
        }
      });
    } else if (s.lat && s.lon) {
      const display = s.display_name || s.description || String(s);
      onChange(display);
      helpers?.setFieldValue("latitude", String(s.lat));
      helpers?.setFieldValue("longitude", String(s.lon));
    } else if (s.description) {
      onChange(s.description);
    }
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const previewSuggestion = (s: any) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (key && googleReady.current && s.place_id && (window as any).google) {
      const ps = new (window as any).google.maps.places.PlacesService(
        document.createElement("div"),
      );
      ps.getDetails({ placeId: s.place_id }, (place: any, status: any) => {
        if (
          status ===
            (window as any).google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry &&
          place.geometry.location
        ) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          helpers?.setFieldValue("latitude", String(lat));
          helpers?.setFieldValue("longitude", String(lng));
        }
      });
    } else if (s.lat && s.lon) {
      helpers?.setFieldValue("latitude", String(s.lat));
      helpers?.setFieldValue("longitude", String(s.lon));
    }
  };

  return (
    <Box>
      <CustomTextField
        label="Address"
        value={value || ""}
        onChange={(e: any) => {
          const v = e.target.value;
          onChange(v);
          triggerGeocode(v);
        }}
        onBlur={() => {
          setTimeout(() => setShowSuggestions(false), 150);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true);
          if (value && String(value).trim().length >= 3) {
            triggerGeocode(String(value));
          }
        }}
        fullWidth
      />

      {showSuggestions && (
        <Paper
          square
          elevation={2}
          sx={{ mt: 1, maxHeight: 220, overflow: "auto" }}
        >
          {loadingSuggest && (
            <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              <Typography variant="body2">Searching Lebanon...</Typography>
            </Box>
          )}
          <List dense>
            {suggestions.map((s: any, idx: number) => (
              <ListItem
                key={`${s.place_id ?? s.osm_id ?? idx}-${idx}`}
                disablePadding
              >
                <ListItemButton
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => handleSelectSuggestion(s)}
                  onMouseEnter={() => previewSuggestion(s)}
                >
                  <ListItemText
                    primary={
                      s.description ||
                      s.display_name ||
                      s.description ||
                      s.name ||
                      s.formatted_address ||
                      s.value
                    }
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {!loadingSuggest && suggestions.length === 0 && (
              <ListItem>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No results in Lebanon
                </Typography>
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      <Box
        sx={{
          mt: 1,
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${COLORS.BorderColor}`,
        }}
      >
        <DraggableMap
          lat={Number.isFinite(latNum as number) ? (latNum as number) : 33.8886}
          lng={Number.isFinite(lngNum as number) ? (lngNum as number) : 35.4955}
          onChange={async (lat, lng) => {
            helpers?.setFieldValue("latitude", String(lat));
            helpers?.setFieldValue("longitude", String(lng));
            try {
              const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
              if (key && googleReady.current && (window as any).google) {
                // reverse geocode using Google Geocoder
                const geocoder = new (window as any).google.maps.Geocoder();
                geocoder.geocode(
                  { location: { lat, lng } },
                  (results: any[]) => {
                    if (results && results[0]) {
                      onChange(results[0].formatted_address);
                    }
                  },
                );
              } else {
                const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`;
                const resp = await fetch(url, {
                  headers: {
                    Accept: "application/json",
                    "Accept-Language": "en",
                    "User-Agent": "PadelFrontendDash/1.0",
                  },
                });
                if (resp.ok) {
                  const data = await resp.json();
                  const display = data?.display_name as string | undefined;
                  if (display) onChange(display);
                }
              }
            } catch (err) {
              // ignore
            }
          }}
          height={280}
        />
      </Box>
    </Box>
  );
};

const DASHBOARD_PAGE_SIZE = 30;

const Dashboard: React.FC = () => {
  const params = useParams<{ businessId?: string; id?: string }>();
  const venueId = params.businessId || params.id;
  const basePath = venueId ? `/dashboard/${venueId}` : "/dashboard";
  if (!venueId) {
    throw new Error("Clients page requires venueId in route");
  }
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [activeUnit, setActiveUnit] = React.useState<string | null>(null);
  const [unitList, setUnitList] = React.useState<string[]>([]);
  const [unitClientCounts, setUnitClientCounts] = React.useState<
    Record<string, number>
  >({});
  const [unitStatsLoading, setUnitStatsLoading] = React.useState(false);
  const navigate = useNavigate();
  const [serviceOptions, setServiceOptions] = useState<
    { key: string; label: string }[]
  >([]);
  const [editingServiceOptions, setEditingServiceOptions] = useState<
    { key: string; label: string }[]
  >([]);
  const [editingServices, setEditingServices] = useState<Service[]>([]);

  const [search, setSearch] = React.useState<string>("");
  const [dateRange, setDateRange] = React.useState<{
    start?: string;
    end?: string;
  } | null>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const localDate = `${year}-${month}-${day}`;
    return { start: localDate, end: localDate };
  });
  type Unit = {
    name: string;
    sender: string;
    description: string;
    address: string;
    priorCancellationHours: string;
    phone: string;
    _id: string;
    id?: string;
    venue_id?: string;
    order?: number | string;
    orderNumber?: number | string;
    order_number?: number | string;
    banner?: string;
    business_id?: string;
  };
  const [units, setUnits] = useState<Unit[]>([]);
  const [businessTypes, setBusinessTypes] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedCourt, setSelectedCourt] = useState<string>("all");

  const selectedCourtRef = useRef<string>(selectedCourt);

  const [dialogOpen, setDialogOpen] = useState(false);

  const convertUtcTimeToLocal = (date: string, time: string) => {
    // Parse incoming UTC time string and present as 12h local label
    const utc = new Date(`${date}T${time || "00:00"}:00Z`);
    const minutesLocal = utc.getHours() * 60 + utc.getMinutes();
    return formatMinutesTo12h(minutesLocal);
  };

  const normalizeDateInput = (
    value: any,
    endOfDay = false,
  ): string | undefined => {
    if (!value) return undefined;

    let year: number | undefined;
    let month: number | undefined; // 0-indexed
    let day: number | undefined;

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split("-").map(Number);
      year = y;
      month = m - 1;
      day = d;
    } else {
      const parsed = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(parsed.getTime())) return undefined;
      year = parsed.getUTCFullYear();
      month = parsed.getUTCMonth();
      day = parsed.getUTCDate();
    }

    const dateUtc = Date.UTC(
      year!,
      month!,
      day!,
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 999 : 0,
    );

    return new Date(dateUtc).toISOString();
  };

  const getDateRangeValues = () => {
    if (!dateRange) return { start: undefined, end: undefined };
    const start = normalizeDateInput(dateRange.start);
    const end = normalizeDateInput(dateRange.end, true);

    if (start && end) {
      const startTime = new Date(start).getTime();
      const endTime = new Date(end).getTime();
      if (!Number.isNaN(startTime) && !Number.isNaN(endTime)) {
        if (startTime > endTime) {
          return { start: end, end: start };
        }
      }
    }

    return { start, end };
  };

  // The table fetches one page at a time itself; this only tells it, and the
  // per-branch "today" counts, to load again after a change.
  const [appointmentsRefreshKey, setAppointmentsRefreshKey] = useState(0);
  const fetchDashboardData = async () => {
    setAppointmentsRefreshKey((key) => key + 1);
  };

  // Every appointment in the date range. Only the CSV export needs them all.
  const fetchAllDashboardRows = async (
    reportProgress?: ExportProgressReporter,
  ): Promise<any[]> => {
    const { start, end } = getDateRangeValues();
    const pageSize = 200;
    const all: any[] = [];
    for (let page = 0; page < 200; page += 1) {
      const data = (await getDashboardAppointments({
        businessId: venueId,
        startDate: start,
        endDate: end,
        page,
        size: pageSize,
        sort: "startAt,asc",
      })) as any;

      const content = (data?.content ?? data?.data?.content ?? []) as any[];
      if (Array.isArray(content) && content.length > 0) {
        all.push(...content);
      }

      const totalPages = data?.totalPages ?? data?.data?.totalPages;
      if (typeof totalPages === "number" && Number.isFinite(totalPages)) {
        reportProgress?.(page + 1, totalPages);
        if (page >= totalPages - 1) break;
      } else if (!Array.isArray(content) || content.length < pageSize) {
        break;
      }
    }
    return all;
  };

  // "Today's Appointments" per branch: one count request per branch.
  const [todayCountsByUnit, setTodayCountsByUnit] = useState<
    Record<string, number>
  >({});
  useEffect(() => {
    if (!venueId || units.length === 0) return;
    let cancelled = false;
    const today = new Date();
    const startDate = normalizeDateInput(today);
    const endDate = normalizeDateInput(today, true);
    // The cards always counted today's bookings inside the selected range, so
    // a range that leaves today out shows 0, as it always did.
    const { start: rangeStart, end: rangeEnd } = getDateRangeValues();
    if (
      (rangeStart && endDate && endDate < rangeStart) ||
      (rangeEnd && startDate && startDate > rangeEnd)
    ) {
      setTodayCountsByUnit({});
      return;
    }
    Promise.all(
      units.map(async (unit) => {
        const unitId = String(unit.id || unit._id || "");
        if (!unitId) return [unitId, 0] as const;
        try {
          const data = (await getDashboardAppointments({
            businessId: venueId,
            unitId,
            startDate,
            endDate,
            page: 0,
            size: 1,
          })) as any;
          return [
            unitId,
            Number(data?.totalElements ?? data?.data?.totalElements) || 0,
          ] as const;
        } catch {
          return [unitId, 0] as const;
        }
      }),
    ).then((entries) => {
      if (!cancelled) setTodayCountsByUnit(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
    // normalizeDateInput is a plain helper recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, venueId, appointmentsRefreshKey, dateRange]);

  useEffect(() => {
    if (!editOpen || !editingRow) return;

    const fetchEditingServices = async () => {
      try {
        // Get business_id from editing row
        const rowUnitId =
          editingRow.unit_id ||
          editingRow.unit?.id ||
          editingRow.unitId ||
          units.find((u) => u.name === editingRow.unit)?._id ||
          units.find((u) => u.name === editingRow.unit)?.id;

        if (!rowUnitId) {
          console.error("No unit ID found in editing row");
          return;
        }

        const response = (await getAllServices(rowUnitId)) as any;
        const servicesData =
          response?.services ||
          response?.data?.services ||
          response?.data ||
          response ||
          [];

        setEditingServices(servicesData);

        setEditingServiceOptions(
          servicesData.map((s: any) => ({
            key: s.id || s._id,
            label: s.name || s.title || "",
          })),
        );

        // Set price from service_id
        if (editingRow.service_id) {
          const svc = servicesData.find(
            (s: any) => String(s.id || s._id) === String(editingRow.service_id),
          );

          if (svc) {
            setEditingRow((prev: any) => ({
              ...prev,
              servicePrice: String(svc.price || ""),
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch editing services", err);
        setEditingServiceOptions([]);
      }
    };

    fetchEditingServices();
  }, [editOpen, editingRow?.id, units]);

  const fetchClientsPerUnit = async (units: Unit[]) => {
    if (!venueId || units.length === 0) return;

    setUnitStatsLoading(true);

    try {
      const counts: Record<string, number> = {};

      await Promise.all(
        units.map(async (unit) => {
          const unitId = String(unit.id || unit._id);
          if (!unitId) return;

          // Only the count is shown, so ask for one row and read the total.
          const res = await getClientsPage({
            businessId: String(venueId),
            unitId,
            page: 0,
            size: 1,
          });
          counts[unitId] = Number(res?.totalElements) || 0;
        }),
      );

      setUnitClientCounts(counts);
    } catch (err) {
      console.error("Failed to fetch clients per unit", err);
    } finally {
      setUnitStatsLoading(false);
    }
  };

  const fetchBranches = async () => {
    if (!venueId) return;

    setUnitStatsLoading(true);

    try {
      const response = await getAllBranches(venueId);

      const UnitsData: Unit[] =
        response?.units ||
        response?.data?.units ||
        response?.data ||
        response ||
        [];

      const sorted = sortUnitsByOrder(UnitsData);

      setUnits(sorted);
      setUnitList(buildOrderedUnitNames(sorted));
      fetchClientsPerUnit(sorted);
    } catch (err) {
      console.error("Failed to fetch units", err);
    } finally {
      setUnitStatsLoading(false);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const response = await getAllBusinessTypes();
      console.log("Business types response:", response);
      if (Array.isArray(response)) {
        const normalized = response
          .map((type: any) => {
            if (typeof type === "string") {
              return {
                id: type,
                name: type.replace(/_/g, " "),
              };
            }

            const id =
              type?.id ??
              type?.type_id ??
              type?._id ??
              type?.value ??
              type?.uuid;

            const name =
              type?.name ??
              type?.value ??
              type?.type_name ??
              type?.label ??
              type?.title ??
              type?.description;

            if (!id || !name) return null;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];

        setBusinessTypes(normalized);
      } else {
        setBusinessTypes([]);
      }
    } catch (error) {
      console.error("Failed to fetch business types:", error);
      setBusinessTypes([]);
    }
  };

  React.useEffect(() => {
    fetchBusinessTypes();
  }, []);

  React.useEffect(() => {
    if (venueId) {
      fetchBranches();
    }
  }, [venueId]);

  const formFields: Field[] = [
    {
      name: "unit_id",
      label: "Unit",
      disabled: true,
      type: "dropdown",
      options: units
        .filter((b) => b && b.name)
        .map((unit) => ({
          label: String(unit.name),
          value: String(unit._id || unit.id || unit.name),
        })),
    },
    { name: "date", label: "Date", type: "date" },

    {
      name: "service_id",
      label: "Service",
      type: "dropdown",
      customRender: (value, onChange, helpers) => {
        const opts = editingServiceOptions.map((s) => ({
          label: s.label,
          value: s.key,
        }));
        return (
          <>
            <CustomLabel text="Service" fontWeight={500} fontSize={14} />

            <CustomSelect
              label="Service"
              value={value || ""}
              options={opts}
              onChange={(v: any) => {
                const str = String(v || "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("service_id", str);
                  try {
                    const svc = editingServices.find(
                      (s) =>
                        String(s.id || s._id || s.service_id || "") ===
                        String(str),
                    );
                    if (svc) {
                      const price =
                        svc.price ??
                        svc.serviceprice ??
                        svc.servicePrice ??
                        svc.price_discount ??
                        "";
                      helpers.setFieldValue(
                        "servicePrice",
                        String(price ?? ""),
                      );
                    }
                  } catch (err) {}
                  const currentStart =
                    helpers.values && helpers.values["time_start"]
                      ? helpers.values["time_start"]
                      : formTimeStart;
                  try {
                    const dur = findServiceDuration(str, editingServices);
                    const durationMinutes = Number(dur) || 60;
                    const startMinutes = parseTimeToMinutes(
                      String(currentStart),
                    );
                    const desiredEnd = startMinutes + durationMinutes;
                    const clampedEnd = Math.min(
                      desiredEnd,
                      startMinutes + MAX_SERVICE_DURATION_MIN,
                    );
                    helpers.setFieldValue(
                      "time_end",
                      formatMinutesTo12h(clampedEnd),
                    );
                  } catch (e) {}
                }
              }}
              fullWidth
            />
          </>
        );
      },
    },

    {
      name: "servicePrice",
      label: "Service Price",
      type: "text",
      disabled: true,
      customRender: (value) => {
        const raw = value === undefined || value === null ? "" : String(value);
        const display = raw && !raw.trim().endsWith("$") ? `${raw} $` : raw;
        return (
          <CustomTextField
            label="Service Price"
            value={display}
            disabled
            fullWidth
          />
        );
      },
    },
    { name: "time_start", label: "Start Time", disabled: true },
    { name: "time_end", label: "End Time", disabled: true },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: ["PENDING", "IN_PROGRESS", "DONE", "CANCELLED"],
    },
    { name: "notes", label: "Notes" },
  ];

  const handleDialogSubmit = async (values: Record<string, any>) => {
    try {
      // Validation for required fields
      const requiredFields = [
        { key: "name", label: IS_CLINIC ? "Name" : "Branch Name" },
        { key: "short_name", label: "Short Name" },
        { key: "orderNumber", label: "Order Number" },
        { key: "type_id", label: "Business Type" },
      ];

      const missingFields: string[] = [];
      for (const field of requiredFields) {
        const value = values[field.key];
        if (!value || String(value).trim() === "") {
          missingFields.push(field.label);
        }
      }

      if (missingFields.length > 0) {
        toast.error(
          `Please fill in the following required fields: ${missingFields.join(
            ", ",
          )}`,
          {
            position: "top-right",
          },
        );
        return;
      }

      const formData = new FormData();

      const fieldMapping: Record<string, string> = {
        name: "name",
        short_name: "short_name",
        description: "description",
        address: "address",
        fn_number: "fn_number",
        phoneNumber: "unit_phone",
        countryCode: "countryCode",
        prior_cancel_hours: "prior_cancel_hours",
        latitude: "latitude",
        longitude: "longitude",
        banner: "banner",
        orderNumber: "orderNumber",
      };

      let unitPhone = "";
      if (values.countryCode && values.phoneNumber) {
        const countryCode = values.countryCode.replace(/\s/g, "");
        unitPhone = `${countryCode}${values.phoneNumber}`;
      } else if (values.phoneNumber) {
        unitPhone = values.phoneNumber;
      }

      if (venueId) {
        formData.append("businessId", venueId);
      }

      if (values.type_id) {
        const selectedType = businessTypes.find(
          (type) => String(type.id) === String(values.type_id),
        );
        const typeIdToSend = selectedType?.id ?? String(values.type_id);
        const typeNameToSend = selectedType?.name ?? String(values.type_id);

        formData.append("type_id", typeIdToSend);
        formData.append("type", typeNameToSend);
      }

      Object.keys(values).forEach((key) => {
        const value = values[key];
        const backendFieldName = fieldMapping[key] || key;

        if (key === "banner") {
          if (value && value instanceof File) {
            formData.append("banner", value);
          }
        } else if (
          key === "phoneNumber" ||
          key === "countryCode" ||
          key === "type_id"
        ) {
          return;
        } else if (value !== undefined && value !== null && value !== "") {
          formData.append(backendFieldName, String(value));
        }
      });

      if (unitPhone) {
        formData.append("unit_phone", unitPhone);
      }

      await createBranch(formData);
      toast.success("Unit created successfully!", { position: "top-right" });
      fetchBranches();
      setDialogOpen(false);
    } catch (error: any) {
      console.error("Failed to create unit:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to create unit.";
      showErrorToast(message, { position: "top-right" });
    }
  };
  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };

  //edit invoice logic
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any | null>(null);
  const [invoiceServices, setInvoiceServices] = useState<Service[]>([]);
  const [discount, setDiscount] = useState(0);
  const [vat, setVat] = useState(0);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [invoiceClients, setInvoiceClients] = useState<any[]>([]);

  const ALL_INVOICE_STATUSES = [
    "PENDING",
    "PAID",
    "PARTIALLY_PAID",
    "UNPAID",
    "REFUNDED",
    "PARTIALLY_REFUNDED",
    "CANCELLED",
  ];

  const extractStatusString = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (value && typeof value === "object") {
      const obj = value as Record<string, unknown>;
      const raw =
        obj.status ??
        obj.name ??
        obj.label ??
        obj.title ??
        obj.code ??
        obj.value ??
        obj.id ??
        obj.status_id;
      return typeof raw === "string" || typeof raw === "number" ? String(raw) : "";
    }
    return "";
  };

  // Appointment-style restrictions: only allow simple actions from this modal.
  const APPOINTMENT_ALLOWED_TARGETS = new Set([
    "PAID",
    "UNPAID",
    "CANCELLED",
  ]);

  const ALLOWED_STATUS_TRANSITIONS = new Set<string>([
    "PENDING->PENDING",
    "PENDING->PAID",
    "PENDING->CANCELLED",
    "UNPAID->UNPAID",
    "UNPAID->PAID",
    "UNPAID->CANCELLED",
    "PARTIALLY_PAID->PARTIALLY_PAID",
    "PARTIALLY_PAID->PAID",
    "PARTIALLY_PAID->UNPAID",
    "PARTIALLY_PAID->CANCELLED",
    "PAID->PAID",
    "PAID->UNPAID",
    "PAID->REFUNDED",
    "PAID->CANCELLED",
    "REFUNDED->REFUNDED",
    "REFUNDED->PAID",
    "REFUNDED->CANCELLED",
    "PARTIALLY_REFUNDED->PARTIALLY_REFUNDED",
    "PARTIALLY_REFUNDED->PAID",
    "PARTIALLY_REFUNDED->REFUNDED",
    "PARTIALLY_REFUNDED->UNPAID",
    "PARTIALLY_REFUNDED->CANCELLED",
    "CANCELLED->CANCELLED",
    "CANCELLED->PAID",
  ]);

  type InvoiceClientOption = {
    id: string;
    name: string;
    email?: string;
    mobile?: string;
  };

  const handleInvoiceDialogClose = () => {
    setInvoiceEditOpen(false);
    setEditingInvoice(null);
    setInvoiceItems([]);
  };

  const fetchInvoiceServices = async (
    unitId?: string | number | null,
    businessId?: string | number | null,
  ) => {
    if (unitId) {
      try {
        const response = (await getAllServices(String(unitId))) as any;
        const servicesData =
          response?.data?.services ||
          response?.services ||
          (Array.isArray(response) ? response : []);
        setInvoiceServices(Array.isArray(servicesData) ? servicesData : []);
        return;
      } catch (error) {
        console.error("Error fetching services for invoice edit:", error);
      }
    }

    if (businessId) {
      try {
        const response = (await getAllServices(String(businessId))) as any;
        const servicesData =
          response?.data?.services ||
          response?.services ||
          (Array.isArray(response) ? response : []);
        setInvoiceServices(Array.isArray(servicesData) ? servicesData : []);
        return;
      } catch (error) {
        console.error("Error fetching services for invoice edit:", error);
      }
    }

    setInvoiceServices([]);
  };

  const formatInvoiceDateValue = (value: any): string => {
    if (!value) return "";
    const dateObj = value instanceof Date ? value : new Date(value);
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  };

    const resolveInvoiceStatusFormValue = (
      status: unknown,
      fallbackId?: unknown,
    ):
    | "PAID"
    | "PARTIALLY_PAID"
    | "UNPAID"
    | "CANCELLED"
    | "REFUNDED"
      | "PARTIALLY_REFUNDED"
      | "PENDING" => {
      const normalize = (val: any) =>
        extractStatusString(val).trim().toUpperCase();

        const normalizedStatus = normalize(status);
        const normalizedFallback = normalize(fallbackId);
        const allowed = new Set([
          "PAID",
          "PARTIALLY_PAID",
          "UNPAID",
          "CANCELLED",
          "REFUNDED",
          "PARTIALLY_REFUNDED",
          "PENDING",
        ]);
        if (normalizedStatus === "VOID" || normalizedStatus === "VOIDED") {
          return "CANCELLED";
        }
        if (normalizedFallback === "VOID" || normalizedFallback === "VOIDED") {
          return "CANCELLED";
        }

        if (normalizedStatus === "CANCELLED") return "CANCELLED";
        if (normalizedStatus) {
          if (allowed.has(normalizedStatus)) {
            return normalizedStatus as
              | "PAID"
              | "PARTIALLY_PAID"
              | "UNPAID"
            | "CANCELLED"
            | "REFUNDED"
            | "PARTIALLY_REFUNDED"
            | "PENDING";
        }
      }

      const numericFromStatusRaw = Number(status);
      const numericFromStatus =
        Number.isFinite(numericFromStatusRaw)
          ? numericFromStatusRaw
          : Number(extractStatusString(status));
      const numericFromFallbackRaw = Number(fallbackId);
      const numericFromFallback =
        Number.isFinite(numericFromFallbackRaw)
          ? numericFromFallbackRaw
          : Number(extractStatusString(fallbackId));
      const numeric = Number.isFinite(numericFromStatus)
        ? numericFromStatus
        : numericFromFallback;
        if (Number.isFinite(numeric)) {
          if (numeric === 1) return "PAID";
          if (numeric === 2) return "UNPAID";
          if (numeric === 3) return "CANCELLED";
        }

        if (allowed.has(normalizedFallback)) {
          return normalizedFallback as any;
        }
        return "UNPAID";
      };

  const resolveInvoicePaymentMethodValue = (value: unknown): string => {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      if (numeric === 1) return "card";
      if (numeric === 2) return "cash";
      if (numeric === 3) return "bank transfer";
    }
    if (typeof value === "string" && value.trim()) {
      const normalized = value.trim().toLowerCase();
      if (
        normalized === "cash" ||
        normalized === "card" ||
        normalized === "bank transfer"
      ) {
        return normalized;
      }
    }
    return "cash";
  };

  const invoiceFormFields: Field[] = React.useMemo(() => {
    const clientOptions = (invoiceClients || []).map(
      (c: InvoiceClientOption) => ({
        label: c.name || c.email || `Client ${c.id}`,
        value: String(c.id),
      }),
    );

    const normalizeInvoiceStatus = (status: any) => {
      const normalized = extractStatusString(status).trim().toUpperCase();
      if (normalized === "VOID" || normalized === "VOIDED") return "CANCELLED";
      return normalized;
    };

    const currentInvoiceStatus = normalizeInvoiceStatus(
      editingInvoice?.invoice?.status ||
        editingInvoice?.status ||
        editingInvoice?.invoice?.status_name ||
        editingInvoice?.status_name ||
        "PENDING",
    );

    const filteredStatusOptions =
      editingInvoice && currentInvoiceStatus
        ? ALL_INVOICE_STATUSES.filter((status) =>
            ALLOWED_STATUS_TRANSITIONS.has(
              `${currentInvoiceStatus}->${normalizeInvoiceStatus(status)}`,
            ),
          )
        : ALL_INVOICE_STATUSES;

    const safeStatusOptions =
      filteredStatusOptions.length > 0
        ? filteredStatusOptions
        : ALL_INVOICE_STATUSES;

    let trimmedStatusOptions = safeStatusOptions.filter((status) => {
      const normalized = normalizeInvoiceStatus(status);
      return (
        APPOINTMENT_ALLOWED_TARGETS.has(normalized) ||
        normalized === currentInvoiceStatus
      );
    });

    if (currentInvoiceStatus === "PENDING") {
      trimmedStatusOptions = trimmedStatusOptions.filter((status) => {
        const normalized = normalizeInvoiceStatus(status);
        return normalized === "PENDING" || normalized === "PAID";
      });
    }

    return [
      {
        name: "clientName",
        label: "Client",
        type: "dropdown",
        options: clientOptions,
        searchable: true,
        customRender: (value, onChange, helpers, error) => {
          return (
            <>
              <CustomLabel text="Client" fontWeight={500} fontSize={14} />
              <CustomSelect
                label="Client"
                value={value || ""}
                options={clientOptions}
                searchable={false}
                disabled
                error={Boolean(error)}
                helperText={error}
                onChange={(v: any) => {
                  const str = String(v ?? "");
                  if (helpers && helpers.setFieldValue) {
                    helpers.setFieldValue("clientName", str);
                  } else {
                    onChange(str);
                  }
                }}
                fullWidth
                placeholder="Select..."
              />
            </>
          );
        },
      },
      { name: "email", label: "Email", disabled: true },
      { name: "phoneNumber", label: "Phone Number", disabled: true },
      { name: "invoice_date", label: "Date", type: "date" },
      {
        name: "status",
        label: "Status",
        type: "dropdown",
        options: trimmedStatusOptions,
      },
      {
        name: "paymentMethod",
        label: "Payment method",
        type: "dropdown",
        options: ["cash"],
        disabled: true,
      },
      { name: "notes", label: "Notes", colSpan: 2 },
      {
        name: "items",
        label: "Items",
        colSpan: 2,
        customRender: (_value: any, onChange: (v: any) => void, helpers) => (
          <InvoiceItemsEditor
            value={_value}
            onChange={onChange}
            vatValue={helpers?.values?.vat || 0}
            onVatChange={(v) => helpers?.setFieldValue("vat", v)}
            discountValue={helpers?.values?.discount || 0}
            onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
            promoDiscountValue={helpers?.values?.promoDiscount || 0}
            onPromoDiscountChange={(v) =>
              helpers?.setFieldValue("promoDiscount", v)
            }
            promoValue={helpers?.values?.promoValue || 0}
            promoType={helpers?.values?.promoType || ""}
            onPromoValueChange={(v) => helpers?.setFieldValue("promoValue", v)}
            isEditMode
            servicesList={invoiceServices}
          />
        ),
      },
    ];
  }, [invoiceClients, invoiceServices]);

  const invoiceDialogInitialValues = React.useMemo(() => {
    if (!editingInvoice) {
      return {
        vat: String(vat || 0),
        discount: String(discount || 0),
        paymentMethod: "cash",
      };
    }

    const clientId =
      editingInvoice.invoice?.client_id ||
      editingInvoice.client_id ||
      editingInvoice.metadata?.clientOrgUserId ||
      editingInvoice.clientOrgUserId ||
      editingInvoice.client?.id ||
      "";
    const matchedClient = (invoiceClients || []).find(
      (c: InvoiceClientOption) => String(c.id || "") === String(clientId || ""),
    );

    const invoiceDate =
      editingInvoice.invoice?.created_at ||
      editingInvoice.created_at ||
      editingInvoice.invoice?.invoice_date ||
      editingInvoice.invoice_date ||
      editingInvoice.invoice?.issuedAt ||
      editingInvoice.invoice?.createdAt ||
      editingInvoice.invoice?.updatedAt ||
      editingInvoice.invoice?.dueAt ||
      editingInvoice.issuedAt ||
      editingInvoice.createdAt ||
      editingInvoice.updatedAt ||
      editingInvoice.dueAt;

    return {
      clientName: String(clientId || ""),
      email:
        editingInvoice.email ||
        editingInvoice.invoice?.email ||
        editingInvoice.metadata?.email ||
        editingInvoice.client?.email ||
        matchedClient?.email ||
        "",
      phoneNumber:
        editingInvoice.client?.mobile ||
        matchedClient?.mobile ||
        matchedClient?.phone ||
        "",
      invoice_date: formatInvoiceDateValue(invoiceDate),
      status: resolveInvoiceStatusFormValue(
        pickNonEmptyString(
          editingInvoice.status,
          editingInvoice.invoice?.status,
          editingInvoice.invoice?.status_name,
          editingInvoice.invoice?.status_label,
        ),
        editingInvoice.invoice?.status_id ??
          editingInvoice.status_id ??
          editingInvoice.invoice?.status?.id ??
          editingInvoice.status?.id,
      ),
      paymentMethod: "cash",
      notes:
        editingInvoice.invoice?.notes ||
        editingInvoice.notes ||
        editingInvoice.metadata?.notes ||
        "",
      items: invoiceItems,
      invoice_number:
        editingInvoice.invoice?.Invoice_number ||
        editingInvoice.invoice?.invoice_number ||
        editingInvoice.Invoice_number ||
        editingInvoice.invoice_number ||
        editingInvoice.sourceReferenceId,
      appointment_id:
        editingInvoice.invoice?.appointment_id ||
        editingInvoice.appointment_id ||
        editingInvoice.metadata?.appointmentId ||
        editingInvoice.appointmentId ||
        editingInvoice.sourceReferenceId ||
        "",
      net_amount:
        editingInvoice.invoice?.net_amount ||
        editingInvoice.net_amount ||
        editingInvoice.subtotal ||
        editingInvoice.total,
      total_amount:
        editingInvoice.invoice?.total_amount ||
        editingInvoice.total_amount ||
        editingInvoice.total,
      vat: String(
        editingInvoice.invoice?.tax ??
          editingInvoice.tax ??
          editingInvoice.invoice?.Vat ??
          editingInvoice.Vat ??
          vat ??
          0,
      ),
      discount: String(
        editingInvoice.invoice?.discount ??
          editingInvoice.discount ??
          discount ??
          0,
      ),
      promoDiscount: String(
        editingInvoice.promoDiscount ||
          editingInvoice.promo_discount ||
          editingInvoice.invoice?.promoDiscount ||
          editingInvoice.invoice?.promo_discount ||
          0,
      ),
      promoValue: String(
        editingInvoice.promoValue ??
          editingInvoice.promo_value ??
          editingInvoice.invoice?.promoValue ??
          editingInvoice.invoice?.promo_value ??
          0,
      ),
      promoType:
        editingInvoice.promoType ||
        editingInvoice.promo_type ||
        editingInvoice.invoice?.promoType ||
        editingInvoice.invoice?.promo_type ||
        "",
    };
  }, [editingInvoice, invoiceItems, invoiceClients, vat, discount]);

  const invoiceDialogInvoiceNumber = React.useMemo(() => {
    const raw: any = editingInvoice || {};
    return pickNonEmptyString(
      raw?.invoice?.Invoice_number,
      raw?.invoice?.invoice_number,
      raw?.Invoice_number,
      raw?.invoice_number,
      raw?.invoiceId,
      raw?.invoice_id,
      raw?.invoice?.id,
      raw?.id
    );
  }, [editingInvoice]);

  const appointmentDialogInvoiceNumber = React.useMemo(() => {
    const raw: any = editingRow || {};
    return pickNonEmptyString(
      raw?.invoice?.Invoice_number,
      raw?.invoice?.invoice_number,
      raw?.Invoice_number,
      raw?.invoice_number,
      raw?.invoiceId,
      raw?.invoice_id,
      raw?.invoice?.id,
      raw?.invoice?.invoiceId,
      raw?.invoice?.invoice_id
    );
  }, [editingRow]);

  const handleInvoiceSubmit = async (values: Record<string, any>) => {
    const normalizeStatus = (status: any) =>
      typeof status === "string" ? status.trim().toUpperCase() : "";

    const pickNumber = (...vals: any[]) => {
      for (const v of vals) {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
      return 0;
    };

    try {
      if (!editingInvoice) {
        toast.error("No invoice data available.");
        return;
      }

      const invoiceId = editingInvoice.invoice?.id || editingInvoice.id;
      if (!invoiceId) {
        toast.error("Invoice ID not found.");
        return;
      }

      const normalizedItems = Array.isArray(values.items) ? values.items : [];

      if (normalizedItems.some(isInvoiceProductMissingSelection)) {
        toast.error(
          "Choose a product or delete the product row before saving the invoice."
        );
        return;
      }

      const lineItems = normalizedItems.map((item: any) => ({
        quantity: Number(item.quantity),
        amount: Number(item.unitCost),
        discount: getInvoiceItemDiscountAmount(item),
        discountPercent: Number(item.discount || 0),
        net_amount: Number(item.totalPrice),
        itemType: item.itemType || "SERVICE",
        serviceId: item.serviceId || String(item.service),
        serviceLabel: item.serviceLabel || "",
      }));

      const totalAmount = lineItems.reduce(
        (sum: number, item: any) => sum + Number(item.net_amount || 0),
        0,
      );
      const discountPercent = Number(values.discount) || 0;
      const discountAmount =
        Math.round(((totalAmount * discountPercent) / 100) * 100) / 100;
      const amountAfterDiscount = Math.max(0, totalAmount - discountAmount);
      const vatPercent = Number(values.vat) || 0;
      const vatAmount =
        Math.round(((amountAfterDiscount * vatPercent) / 100) * 100) / 100;
      const netAmount =
        Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

      const totalFromApi = pickNumber(
        editingInvoice.invoice?.net_amount,
        editingInvoice.net_amount,
        editingInvoice.invoice?.total_amount,
        editingInvoice.total_amount,
        editingInvoice.invoice?.total,
        editingInvoice.total,
        netAmount,
      );

      const rawRemaining = pickNumber(
        editingInvoice.invoice?.remaining_amount,
        editingInvoice.invoice?.remainingAmount,
        editingInvoice.invoice?.balance,
        editingInvoice.invoice?.outstanding,
        editingInvoice.remaining_amount,
        editingInvoice.balance,
        editingInvoice.outstanding,
      );

      let paidAmount = pickNumber(
        editingInvoice.invoice?.paid_amount,
        editingInvoice.invoice?.paidAmount,
        editingInvoice.invoice?.total_paid,
        editingInvoice.paid_amount,
        editingInvoice.paid,
        editingInvoice.total_paid,
      );

      const refundedAmount = pickNumber(
        editingInvoice.invoice?.refunded_amount,
        editingInvoice.invoice?.refundedAmount,
        editingInvoice.invoice?.total_refunded,
        editingInvoice.invoice?.refund_amount,
        editingInvoice.refunded_amount,
        editingInvoice.total_refunded,
        editingInvoice.refund_amount,
      );

      if (paidAmount === 0 && totalFromApi > 0 && rawRemaining >= 0) {
        const inferredPaid = totalFromApi - rawRemaining;
        if (Number.isFinite(inferredPaid) && inferredPaid > 0) {
          paidAmount = inferredPaid;
        }
      }

      const refundableAmount = Math.max(paidAmount - refundedAmount, 0);

      const computedRemaining = Math.max(
        (totalFromApi || netAmount) - paidAmount + refundedAmount,
        0,
      );

      const payCandidates = [rawRemaining, computedRemaining, netAmount].filter(
        (v) => Number.isFinite(v) && v > 0,
      );
      const minPositive = payCandidates.length ? Math.min(...payCandidates) : 0;
      const amountToPay = rawRemaining > 0 ? rawRemaining : minPositive;

      const amountToRefund =
        paidAmount > 0 ? paidAmount : Math.max(netAmount, 0);

      const currentStatus = normalizeStatus(
        editingInvoice.invoice?.status ||
          editingInvoice.status ||
          editingInvoice.invoice?.status_name ||
          editingInvoice.status_name ||
          "PENDING",
      );
      const requestedStatus = normalizeStatus(
        values.status || currentStatus || "PENDING",
      );

      if (
        requestedStatus !== currentStatus &&
        !APPOINTMENT_ALLOWED_TARGETS.has(requestedStatus)
      ) {
        toast.info(
          "Cannot make this transaction here. Please go to the Invoices page to complete it.",
        );
        return;
      }

      if (currentStatus === "CANCELLED" && requestedStatus !== "CANCELLED") {
        toast.info("Cancelled invoices cannot be changed here.");
        return;
      }

      if (requestedStatus === "CANCELLED" && currentStatus !== "CANCELLED") {
        toast.info(
          "Cancellation is not supported here. Please use the Invoices page.",
        );
        return;
      }

      if (
        currentStatus === "PENDING" &&
        requestedStatus !== "PENDING" &&
        requestedStatus !== "PAID"
      ) {
        toast.info(
          "Pending invoices can only be marked as Paid here. For other actions, go to the Invoices page.",
        );
        return;
      }

      const transition = `${currentStatus}->${requestedStatus}`;
      if (!ALLOWED_STATUS_TRANSITIONS.has(transition)) {
        toast.error(
          `Cannot change invoice status from ${currentStatus} to ${requestedStatus}.`,
        );
        return;
      }

      const needsPayment =
        requestedStatus === "PAID" && currentStatus !== "PAID";
      const needsRefundToUnpaid =
        (currentStatus === "PAID" ||
          currentStatus === "PARTIALLY_PAID" ||
          currentStatus === "PARTIALLY_REFUNDED") &&
        requestedStatus === "UNPAID";
      const needsRefund = needsRefundToUnpaid;

      if (needsPayment && amountToPay <= 0) {
        toast.error(
          "Nothing to pay. Invoice already settled or total is zero.",
        );
        return;
      }

      const refundAmountToUse =
        refundableAmount > 0 ? refundableAmount : amountToRefund;

      if (needsRefund && refundAmountToUse <= 0) {
        toast.error("Nothing to refund for this invoice.");
        return;
      }

      const branchSelection =
        activeUnit ||
        editingInvoice?.unit?.id ||
        editingInvoice?.unitId ||
        editingInvoice?.unit_id ||
        editingInvoice?.invoice?.unitId ||
        editingInvoice?.invoice?.unit_id ||
        null;

      const branchRecord = branchSelection
        ? units.find(
            (b) =>
              String(b.id || b._id) === String(branchSelection) ||
              String(b._id || b.id) === String(branchSelection),
          )
        : undefined;

      let businessIdToUse =
        editingInvoice?.business_id ||
        editingInvoice?.invoice?.business_id ||
        editingInvoice?.businessId ||
        branchRecord?.business_id ||
        branchRecord?.id ||
        venueId;

      if (!businessIdToUse && branchRecord) {
        businessIdToUse = branchRecord.business_id || branchRecord.id;
      }

      const unitIdToUse =
        branchSelection ||
        editingInvoice?.unit?.id ||
        editingInvoice?.unitId ||
        editingInvoice?.unit_id ||
        editingInvoice?.invoice?.unitId ||
        editingInvoice?.invoice?.unit_id ||
        branchRecord?._id ||
        venueId;

      const itemsPayload = lineItems.map((item: any) => {
        const isProduct = item.itemType === "PRODUCT";
        return {
          description: item.serviceLabel || (isProduct ? "Product" : "Service"),
          qty: Number(item.quantity) || 1,
          unitPrice: Number(item.amount) || 0,
          discount: Number(item.discount || 0),
          metadata: {
            type: isProduct ? "PRODUCT" : "SERVICE",
            discountPercent: Number(item.discountPercent || 0),
            ...(isProduct
              ? {
                  productId: item.serviceId,
                  businessId: businessIdToUse,
                  stockDirection: "OUT",
                }
              : { serviceId: item.serviceId }),
          },
        };
      });

      const payload = {
        userId: values.clientName, // Billing user ID
        currency: "USD",
        discount: discountPercent,
        tax: vatPercent,
        issuedAt: values.invoice_date,
        status: requestedStatus,
        sourceReferenceId:
          values.appointment_id ||
          editingInvoice?.invoice?.appointment_id ||
          editingInvoice?.appointment_id ||
          null,
        metadata: {
          clientOrgUserId: values.clientName, // CORE client UUID
          businessId: businessIdToUse, // BACKEND business (venueId in FE)
          unitId: unitIdToUse, // BACKEND unit (branch in FE)
          notes: values.notes,
        },
        items: itemsPayload,
      };

      const skipUpdate = needsRefund;
      let updateResponse: any = null;

      if (!skipUpdate) {
        updateResponse = await updateQuotation(Number(invoiceId), payload);
      }

      if (needsPayment) {
        const updatedAmountToPay = pickNumber(
          updateResponse?.updated?.balance,
          updateResponse?.balance,
          updateResponse?.updated?.total,
          updateResponse?.total,
          amountToPay
        );
        await payInvoice(String(invoiceId), {
          amount: updatedAmountToPay,
          method: "CASH",
          provider: "MANUAL",
          status: "SUCCESS",
        });
      } else if (needsRefund) {
        await refundInvoice(String(invoiceId), {
          amount: refundAmountToUse,
          status: "SUCCESS",
        });
        if (needsRefundToUnpaid) {
          setEditingInvoice((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              status: "UNPAID",
              status_name: "UNPAID",
              invoice: {
                ...prev.invoice,
                status: "UNPAID",
                status_name: "UNPAID",
              },
            };
          });
        }
      }

      const actionMessage = needsPayment
        ? "Invoice fully paid."
        : needsRefundToUnpaid
          ? "Invoice marked unpaid."
          : "Invoice updated successfully!";

      toast.success(actionMessage);
      handleInvoiceDialogClose();
      await fetchDashboardData();
      await fetchRevenueSummary();
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Failed to update invoice.");
    }
  };

  const calculateItemTotalPrice = (
    quantity: number,
    unitCost: number,
    discount: number,
  ) => {
    const qty = Number(quantity) || 0;
    const unit = Number(unitCost) || 0;
    const discountPercent = Math.min(100, Math.max(0, Number(discount) || 0));
    const subtotal = qty * unit;
    const discountAmount = (subtotal * discountPercent) / 100;
    return Math.round((subtotal - discountAmount) * 100) / 100;
  };
  const roundInvoiceMoney = (value: number) =>
    Math.round((Number(value) || 0) * 100) / 100;
  const pickFiniteNumber = (...values: any[]) => {
    for (const value of values) {
      if (value === undefined || value === null || value === "") continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
    return undefined;
  };
  const getInvoiceItemDiscountAmount = (item: any) => {
    const subtotal = (Number(item?.quantity) || 0) * (Number(item?.unitCost) || 0);
    const totalPrice = Number(item?.totalPrice);

    if (Number.isFinite(totalPrice)) {
      return roundInvoiceMoney(Math.max(subtotal - totalPrice, 0));
    }

    return roundInvoiceMoney(
      (subtotal * Math.min(100, Math.max(0, Number(item?.discount) || 0))) / 100,
    );
  };
  const resolveInvoiceItemDiscountPercent = (
    item: any,
    quantity: number,
    unitCost: number,
    totalPrice: number,
    promoType?: string,
    promoValue?: number,
  ) => {
    const explicitPercent = Number(
      item?.discountPercent ??
        item?.discount_percent ??
        item?.metadata?.discountPercent ??
        item?.metadata?.discount_percent,
    );
    if (Number.isFinite(explicitPercent)) {
      return Math.min(100, Math.max(0, explicitPercent));
    }

    const subtotal = (Number(quantity) || 0) * (Number(unitCost) || 0);
    const normalizedPromoType = String(promoType || "").trim().toUpperCase();
    const numericPromoValue = Number(promoValue);
    if (
      subtotal > 0 &&
      Number.isFinite(numericPromoValue) &&
      numericPromoValue > 0
    ) {
      if (normalizedPromoType === "PERCENT") {
        return Math.min(100, Math.max(0, numericPromoValue));
      }
      if (normalizedPromoType === "FLAT") {
        const lineDiscountAmount = Number(item?.discount);
        const flatDiscountAmount =
          Number.isFinite(lineDiscountAmount) && lineDiscountAmount > 0
            ? lineDiscountAmount
            : numericPromoValue;
        return roundInvoiceMoney(
          Math.min(100, Math.max(0, (flatDiscountAmount / subtotal) * 100)),
        );
      }
    }

    if (subtotal > 0 && Number.isFinite(totalPrice)) {
      return roundInvoiceMoney(
        (Math.max(subtotal - totalPrice, 0) / subtotal) * 100,
      );
    }

    return Math.min(100, Math.max(0, Number(item?.discount) || 0));
  };

  const parseServiceLabelValue = (label: unknown): number | undefined => {
    const match = String(label ?? "").match(/\d+/);
    if (!match) return undefined;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const resolveStatusFormValue = (
    statusValue?: unknown,
    statusId?: unknown,
  ):
    | "paid"
    | "unpaid"
    | "pending"
    | "cancelled"
    | "refunded"
    | "partially_refunded"
    | "partially_paid" => {
    const normalize = (
      value: unknown,
    ):
      | "paid"
      | "unpaid"
      | "pending"
      | "cancelled"
      | "refunded"
      | "partially_refunded"
      | "partially_paid"
      | undefined => {
      if (typeof value === "number") {
        if (value === 1) return "paid";
        if (value === 2) return "unpaid";
        if (value === 3) return "cancelled";
        return undefined;
      }

      if (typeof value === "string") {
        const numeric = Number(value);
        if (!Number.isNaN(numeric)) {
          return normalize(numeric);
        }
        const normalized = value.trim().toLowerCase();
        if (normalized === "void" || normalized === "voided") {
          return "cancelled";
        }
        if (
          normalized === "paid" ||
          normalized === "unpaid" ||
          normalized === "pending" ||
          normalized === "cancelled" ||
          normalized === "refunded" ||
          normalized === "partially_refunded" ||
          normalized === "partially paid" ||
          normalized === "partially_paid"
        ) {
          return normalized === "partially paid"
            ? "partially_paid"
            : (normalized as
                | "paid"
                | "unpaid"
                | "pending"
                | "cancelled"
                | "refunded"
                | "partially_refunded"
                | "partially_paid");
        }
      }

      return undefined;
    };

    if (statusValue && typeof statusValue === "object") {
      const statusObject = statusValue as Record<string, unknown>;
      return (
        normalize(
          statusObject.value ||
            statusObject.code ||
            statusObject.status ||
            statusObject.name ||
            statusObject.label ||
            statusObject.title,
        ) ||
        normalize(
          statusObject.id ||
            statusObject.status_id ||
            statusObject.value ||
            statusObject.code,
        ) ||
        normalize(statusId) ||
        "unpaid"
      );
    }

    return normalize(statusValue) || normalize(statusId) || "unpaid";
  };

  const resolvePaymentMethodFormValue = (
    entry?: any,
  ): "cash" | "card" | "bank transfer" => {
    const candidate =
      typeof entry === "object" && entry !== null
        ? (entry.payment_method_id ??
          entry.payment_method?.id ??
          entry.invoice?.payment_method_id ??
          entry.invoice?.payment_method?.id ??
          entry.payment_method ??
          entry.invoice?.payment_method ??
          entry.method ??
          entry.invoice?.method)
        : entry;

    const numeric = Number(candidate);
    if (!Number.isNaN(numeric)) {
      if (numeric === 1) return "card";
      if (numeric === 2) return "cash";
      if (numeric === 3) return "bank transfer";
    }

    if (typeof candidate === "string") {
      const normalized = candidate.trim().toLowerCase();
      if (normalized === "cash") return "cash";
      if (normalized === "card") return "card";
      if (
        normalized === "bank transfer" ||
        normalized === "bank_transfer" ||
        normalized === "bank-transfer"
      ) {
        return "bank transfer";
      }
    }

    return "cash";
  };

  const normalizeInvoiceData = (invoiceData: any) => {
    if (!invoiceData) return invoiceData;

    const client =
      invoiceData.client ||
      invoiceData.client_details ||
      invoiceData.invoice?.client ||
      {};

    const normalizedName =
      pickNonEmptyString(
        invoiceData.name,
        invoiceData.fullName,
        client?.name,
        client?.fullName,
        (client as any)?.full_name,
        invoiceData.client_name,
        (invoiceData as any)?.client_full_name,
      ) || "";

    const normalizedMobile =
      pickNonEmptyString(
        invoiceData.mobile,
        client?.mobile,
        client?.phoneNumber,
        client?.phone,
        (client as any)?.phone,
        invoiceData.client_phone,
      ) || "";

    const normalizedCreatedAt =
      invoiceData.created_at ||
      invoiceData.invoice_date ||
      invoiceData.invoiceDate ||
      invoiceData.invoice?.created_at ||
      invoiceData.invoice?.invoice_date ||
      invoiceData.date ||
      invoiceData.updated_at ||
      null;

    const normalizedId =
      invoiceData?.id ??
      invoiceData?.invoice?.id ??
      invoiceData?.invoice_id ??
      (Array.isArray(invoiceData?.details) &&
      invoiceData.details.length > 0 &&
      invoiceData.details[0]
        ? invoiceData.details[0].invoice_id
        : undefined);

    const statusIdCandidate =
      invoiceData?.status_id ??
      invoiceData?.status?.id ??
      invoiceData?.status?.status_id ??
      invoiceData?.invoice?.status_id ??
      invoiceData?.invoice?.status?.id ??
      invoiceData?.invoice?.status?.status_id;

    const normalizedStatus = resolveStatusFormValue(
      invoiceData?.status ||
        invoiceData?.invoice?.status ||
        invoiceData?.invoice?.status_name ||
        invoiceData?.invoice?.status_label,
      statusIdCandidate,
    );

    const numericStatusId = Number(statusIdCandidate);

    const paymentMethodCandidate =
      invoiceData?.payment_method_id ??
      invoiceData?.payment_method?.id ??
      invoiceData?.invoice?.payment_method_id ??
      invoiceData?.invoice?.payment_method?.id ??
      invoiceData?.method_id ??
      invoiceData?.paymentMethodId ??
      invoiceData?.invoice?.method_id;

    const numericPaymentMethodId = Number(paymentMethodCandidate);

    return {
      ...invoiceData,
      client,
      id: normalizedId ?? invoiceData?.id ?? null,
      name: normalizedName,
      mobile: normalizedMobile,
      created_at: normalizedCreatedAt,
      status: normalizedStatus,
      status_id: Number.isFinite(numericStatusId)
        ? Number(numericStatusId)
        : statusStringToId(normalizedStatus),
      payment_method_id: Number.isFinite(numericPaymentMethodId)
        ? Number(numericPaymentMethodId)
        : paymentMethodStringToId(resolvePaymentMethodFormValue(invoiceData)),
    };
  };
  const statusStringToId = (
    value:
      | "paid"
      | "unpaid"
      | "pending"
      | "cancelled"
      | "refunded"
      | "partially_refunded"
      | "partially_paid",
  ): 1 | 2 | 3 | undefined => {
    if (value === "paid") return 1;
    if (value === "unpaid") return 2;
    if (value === "pending") return undefined;
    if (value === "cancelled") return 3;
    return undefined;
  };

  const paymentMethodStringToId = (
    value: "cash" | "card" | "bank transfer",
  ): 1 | 2 | 3 => {
    if (value === "card") return 2;
    if (value === "bank transfer") return 3;
    return 1;
  };

  const FALLBACK_SERVICE_OPTIONS = [60, 90, 120];

  const openInvoiceEditForId = async (id: string | number) => {
    try {
      setLoadingInvoice(true);
      setInvoiceServices([]);

      // Find the appointment row to get client_id
      const appointmentRow = rows.find((r: any) => String(r.id) === String(id));
      if (!appointmentRow) {
        toast.error("Appointment not found.", { position: "top-right" });
        return;
      }

      const appointmentInvoiceId =
        appointmentRow?.invoiceId ||
        appointmentRow?.invoice_id ||
        appointmentRow?.invoice?.id ||
        appointmentRow?.invoice?.invoiceId ||
        appointmentRow?.invoice?.invoice_id;
      if (!appointmentInvoiceId) {
        toast.error("Invoice ID not found for this appointment.", {
          position: "top-right",
        });
        return;
      }

      const response: any = await getInvoiceById(String(appointmentInvoiceId));
      const invoiceData = response?.data || response;
      if (!invoiceData) {
        toast.error("No invoice found for this appointment.", {
          position: "top-right",
        });
        return;
      }
      const invoiceService =
        invoiceData?.service ||
        invoiceData?.invoice?.service ||
        appointmentRow?.service;
      const appointmentServiceId =
        invoiceService?.id ||
        invoiceService?._id ||
        appointmentRow?.serviceId ||
        appointmentRow?.service_id ||
        appointmentRow?.service?.id ||
        "";
      const appointmentServiceName =
        invoiceService?.name ||
        invoiceService?.title ||
        appointmentRow?.serviceName ||
        appointmentRow?.service_name ||
        appointmentRow?.service?.name ||
        "";
      const appointmentServicePrice = Number(
        invoiceService?.price ||
          appointmentRow?.servicePrice ||
          appointmentRow?.service_price ||
          appointmentRow?.service?.price ||
          0,
      );
      const normalizedInvoice: any = normalizeInvoiceData(invoiceData);
      const safeNormalized = normalizedInvoice || {};
      const invoiceId =
        safeNormalized?.id ||
        safeNormalized?.invoice_id ||
        invoiceData?.invoice_id ||
        invoiceData?.id ||
        invoiceData?.invoice?.id ||
        appointmentInvoiceId;

      // Ensure client_id and appointment_id are set
      const clientId =
        appointmentRow?.client_id ||
        appointmentRow?.clientId ||
        safeNormalized?.client_id ||
        safeNormalized?.clientId;
     if (!clientId) {
        toast.error("Client ID not found for this appointment.", {
          position: "top-right",
         });
         return;
      }
      const appointmentId = id;

      const normalizedBusinessId =
        safeNormalized.business_id ||
        safeNormalized.invoice?.business_id ||
        safeNormalized.invoice?.businessId ||
        safeNormalized.businessId ||
        appointmentRow?.branchId ||
        appointmentRow?.business_id ||
        appointmentRow?.businessId;

      setEditingInvoice({
        ...safeNormalized,
        id: invoiceId,
        invoice_id: invoiceId,
        client_id: clientId,
        appointment_id: appointmentId,
        business_id: normalizedBusinessId,
      });

      const resolvedDiscount =
        Number(
          safeNormalized?.invoice?.discount ??
            safeNormalized?.discount ??
            invoiceData?.invoice?.discount ??
            invoiceData?.discount ??
            0,
        ) || 0;
      const resolvedVat =
        Number(
          safeNormalized?.invoice?.tax ??
            safeNormalized?.tax ??
            invoiceData?.invoice?.tax ??
            invoiceData?.tax ??
            safeNormalized?.invoice?.Vat ??
            safeNormalized?.Vat ??
            invoiceData?.invoice?.Vat ??
            invoiceData?.Vat ??
            invoiceData?.vat ??
            0,
        ) || 0;
      setDiscount(resolvedDiscount);
      setVat(resolvedVat);

      // Initialize invoice items from whichever details array the API returns
      const detailSource =
        safeNormalized.invoice?.items ||
        invoiceData.invoice?.items ||
        safeNormalized.items ||
        invoiceData.items ||
        (safeNormalized && safeNormalized.details) ||
        invoiceData.details ||
        safeNormalized.invoice?.details ||
        invoiceData.invoice?.details ||
        safeNormalized.invoice?.data?.details ||
        invoiceData.invoice?.data?.details ||
        safeNormalized.invoice_details ||
        invoiceData.invoice_details;
      const detailSourceHasProducts =
        Array.isArray(detailSource) &&
        detailSource.some(
          (detail: any) =>
            String(detail?.metadata?.type || detail?.type || "").toUpperCase() ===
            "PRODUCT",
        );

      const productsSource =
        detailSourceHasProducts
          ? []
          : safeNormalized?.products ||
            invoiceData?.products ||
            safeNormalized?.invoice?.products ||
            invoiceData?.invoice?.products ||
            safeNormalized?.metadata?.products ||
            invoiceData?.metadata?.products ||
            [];

      const productItems: InvoiceItem[] = Array.isArray(productsSource)
        ? productsSource.map((p: any, idx: number) => {
            const qty = Number(p?.qty ?? p?.quantity ?? 1) || 1;
            const price = Number(p?.price ?? p?.unitPrice ?? p?.unit_price ?? 0) || 0;
            const totalPrice = Math.round(qty * price * 100) / 100;
            const id =
              p?.id ??
              p?._id ??
              p?.productId ??
              p?.product_id ??
              `${idx + 1}`;
            return {
              id: idx + 1 + 1000,
              service: String(id),
              serviceId: String(id),
              serviceLabel: String(
                p?.name ?? p?.title ?? p?.label ?? p?.description ?? "Product"
              ),
              quantity: qty,
              unitCost: price,
              discount: 0,
              totalPrice,
              itemType: "PRODUCT",
            } as InvoiceItem;
          })
        : [];

      if (detailSource && Array.isArray(detailSource)) {
        const promoDiscountValue =
          Number(
            invoiceData?.promoDiscount ??
              invoiceData?.promo_discount ??
              invoiceData?.invoice?.promoDiscount ??
              invoiceData?.invoice?.promo_discount ??
              safeNormalized?.promoDiscount ??
              safeNormalized?.promo_discount ??
              safeNormalized?.invoice?.promoDiscount ??
              safeNormalized?.invoice?.promo_discount ??
              0,
          ) || 0;
        const invoicePromoType =
          invoiceData?.promoType ??
          invoiceData?.promo_type ??
          invoiceData?.invoice?.promoType ??
          invoiceData?.invoice?.promo_type ??
          safeNormalized?.promoType ??
          safeNormalized?.promo_type ??
          safeNormalized?.invoice?.promoType ??
          safeNormalized?.invoice?.promo_type ??
          "";
        const invoicePromoValue =
          pickFiniteNumber(
            invoiceData?.promoValue,
            invoiceData?.promo_value,
            invoiceData?.invoice?.promoValue,
            invoiceData?.invoice?.promo_value,
            safeNormalized?.promoValue,
            safeNormalized?.promo_value,
            safeNormalized?.invoice?.promoValue,
            safeNormalized?.invoice?.promo_value,
          ) ?? 0;
        const totalQty = Math.max(
          detailSource.reduce(
            (sum: number, d: any) =>
              sum + (Number(d.quantity ?? d.qty ?? 1) || 1),
            0,
          ),
          1,
        );
        const perUnitPromoDiscount =
          promoDiscountValue > 0 ? promoDiscountValue / totalQty : 0;

        const items = detailSource.map((detail: any, index: number) => {
          const serviceLabelRaw =
            pickNonEmptyString(
              detail.serviceLabel,
              detail.service_label,
              detail.service_title,
              detail.service_name,
              detail.title,
            ) || "";
          const numericFromLabel = parseServiceLabelValue(serviceLabelRaw);
          const detailServiceId =
            detail?.metadata?.serviceId ??
            detail?.metadata?.productId ??
            detail.service_id ??
            detail.serviceId ??
            detail.serviceID ??
            detail.productId ??
            detail.product_id ??
            (detail.service && typeof detail.service === "object"
              ? detail.service.id ||
                detail.service._id ||
                detail.service.service_id
              : undefined);
          const detailServiceRaw =
            typeof detail.service === "string" ? detail.service.trim() : "";
          const detailServiceIsId =
            detailServiceRaw && Number.isNaN(Number(detailServiceRaw));
          const normalizedServiceId =
            detailServiceId ??
            (detailServiceIsId ? detailServiceRaw : undefined);
          const normalizedServiceIdText =
            normalizedServiceId !== undefined && normalizedServiceId !== null
              ? String(normalizedServiceId)
              : undefined;
          const derivedServiceValue =
            (normalizedServiceIdText ? normalizedServiceIdText : undefined) ??
            (numericFromLabel && numericFromLabel > 0
              ? numericFromLabel
              : undefined) ??
            (!Number.isNaN(Number(detail.service))
              ? Number(detail.service)
              : undefined) ??
            (!Number.isNaN(Number(detail.service_id))
              ? Number(detail.service_id)
              : undefined) ??
            FALLBACK_SERVICE_OPTIONS[0];
          const quantityValue =
            Number(detail.quantity ?? detail.qty ?? 1) || 1;
          const itemType =
            String(detail?.metadata?.type || detail?.type || "SERVICE").toUpperCase() ===
            "PRODUCT"
              ? "PRODUCT"
              : "SERVICE";
          const baseUnitCost =
            Number(
              detail.amount ??
                detail.unit_cost ??
                detail.unitCost ??
                detail.unitPrice ??
                detail.unit_price ??
                detail.price
            ) || 0;
          const discountValue = Number(detail.discount) || 0;
          const unitCostValue =
            discountValue > 0 || !perUnitPromoDiscount
              ? baseUnitCost
              : Math.max(baseUnitCost - perUnitPromoDiscount, 0);
          const fallbackServiceLabel =
            serviceLabelRaw ||
            (appointmentServiceId &&
            normalizedServiceIdText &&
            String(normalizedServiceIdText) === String(appointmentServiceId)
              ? String(appointmentServiceName || normalizedServiceIdText)
              : String(derivedServiceValue));
          const explicitTotalPrice = pickFiniteNumber(
            detail.totalPrice ??
              detail.total_price ??
              detail.net_amount ??
              detail.total,
          );
          const totalPriceValue =
            explicitTotalPrice ??
            calculateItemTotalPrice(
              quantityValue,
              unitCostValue,
              discountValue,
            );

          return {
            id: index + 1,
            service: derivedServiceValue,
            serviceId: normalizedServiceIdText,
            serviceLabel: fallbackServiceLabel,
            quantity: quantityValue,
            unitCost: unitCostValue,
            discount: resolveInvoiceItemDiscountPercent(
              detail,
              quantityValue,
              unitCostValue,
              totalPriceValue,
              invoicePromoType,
              invoicePromoValue,
            ),
            totalPrice: totalPriceValue,
            itemType,
          } as InvoiceItem;
        });
        setInvoiceItems([...items, ...productItems]);
      } else {
        const serviceUnitCost =
          Number(appointmentServicePrice) ||
          Number(invoiceService?.price) ||
          0;
        // If the backend did not send details, keep existing items to avoid dropping the UI
        const fallbackItems: InvoiceItem[] = [
          {
            id: 1,
            service: appointmentServiceId || 60,
            serviceId: appointmentServiceId ? String(appointmentServiceId) : undefined,
            serviceLabel: String(appointmentServiceName || "60"),
            quantity: 1,
            unitCost: serviceUnitCost || 40,
            discount: 0,
            totalPrice: calculateItemTotalPrice(
              1,
              serviceUnitCost || 40,
              0,
            ),
            itemType: "SERVICE",
          },
        ];
        const mergedFallback = productItems.length
          ? [...fallbackItems, ...productItems]
          : fallbackItems;
        setInvoiceItems((prev) =>
          Array.isArray(prev) && prev.length > 0 ? prev : mergedFallback,
        );
      }

      const unitId =
        invoiceData?.unit?.id ||
        invoiceData?.invoice?.unitId ||
        invoiceData?.invoice?.unit_id ||
        invoiceData?.unitId ||
        invoiceData?.unit_id;

      const businessId =
        invoiceData?.business?.id ||
        invoiceData?.invoice?.business_id ||
        invoiceData?.business_id ||
        invoiceData?.businessId;

      await fetchInvoiceServices(unitId, businessId);

      // Ensure the appointment service is selectable even if services API is missing it.
      if (appointmentServiceId) {
        setInvoiceServices((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const exists = list.some(
            (s: any) => String(s?.id || s?._id || "") === String(appointmentServiceId),
          );
          if (exists) return list;
          return [
            ...list,
            {
              id: String(appointmentServiceId),
              name: String(appointmentServiceName || `Service ${appointmentServiceId}`),
              price: appointmentServicePrice || undefined,
            } as any,
          ];
        });
      }

      setInvoiceEditOpen(true);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      toast.error("Failed to load invoice.", { position: "top-right" });
    } finally {
      setLoadingInvoice(false);
    }
  };

  React.useEffect(() => {
    if (!invoiceEditOpen) return;
    const fetchClients = async () => {
      try {
        const unitId =
          activeUnit ||
          editingInvoice?.unit_id ||
          editingInvoice?.unitId ||
          editingInvoice?.unit?.id ||
          null;
        const forcedClientId =
          editingInvoice?.client?.id ||
          editingInvoice?.client_id ||
          editingInvoice?.invoice?.client_id ||
          editingInvoice?.metadata?.clientOrgUserId ||
          editingInvoice?.clientOrgUserId ||
          null;

        if (!venueId) {
          setInvoiceClients([]);
          return;
        }

        // The picker is locked to the invoice's own client, so that client is
        // all it needs; `limit` keeps the API from sending the whole branch.
        const res = await getClientsSelect({
          businessId: String(venueId),
          unitId: unitId ? String(unitId) : undefined,
          onlyWithAppointments: true,
          ids: forcedClientId ? [String(forcedClientId)] : undefined,
          limit: 1,
        });

        const clientList = Array.isArray(res?.content) ? res.content : [];
        const mapped = clientList
          .map((c: any) => {
            const id = c?.id;
            if (!id) return null;
            return {
              id: String(id),
              name: String(c?.name || c?.label || c?.email || `Client ${id}`),
              email: c?.email || "",
              mobile: c?.mobile || c?.phone || "",
            } as InvoiceClientOption;
          })
          .filter(Boolean);

        setInvoiceClients(mapped as any);
      } catch (error) {
        console.error("Error fetching clients for invoice edit:", error);
        setInvoiceClients([]);
      }
    };

    fetchClients();
  }, [invoiceEditOpen, activeUnit, venueId, editingInvoice]);

  //delete appt
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null,
  );
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onPdf: {
      handler: async (id: number | string) => {
        await openInvoiceEditForId(id);
      },
    },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (units.length > 0) {
      setUnitList(buildOrderedUnitNames(units));
    } else {
      setUnitList([]);
    }
  }, [units]);

  const fields: Field[] = [
    {
      type: "text",
      name: "name",
      // Clinic: the name is what the calendar column shows — a doctor, a room…
      label: IS_CLINIC ? "Name (e.g. Dr. Ahmad or Room 2)" : "Unit Name",
      required: true,
    },
    {
      type: "number",
      name: "orderNumber",
      label: "Order Number",
      required: true,
    },
    { type: "text", name: "short_name", label: "Short Name", required: true },
    {
      name: "type_id",
      label: "Business Type",
      type: "dropdown",
      options: businessTypes.map((type) => ({
        label: String(type.name),
        value: type.id,
      })),
      required: true,
    },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => `+${c.phone}`),
      required: false,
    },
    {
      type: "text",
      name: "phoneNumber",
      label: "Phone Number",
      required: false,
    },
    { type: "text", name: "description", label: "Description" },
    {
      type: "text",
      name: "prior_cancel_hours",
      label: "Prior Cancellation Hours",
    },
    {
      name: "address",
      label: "Address",
      colSpan: 2,
      customRender: (
        value: any,
        onChange: (v: any) => void,
        helpers?: {
          setFieldValue: (name: string, value: any) => void;
          values?: Record<string, any>;
        },
        error?: string,
      ) => {
        return (
          <Box>
            <AddressAutocomplete
              value={value}
              onChange={onChange}
              helpers={helpers}
            />
            {error && (
              <Box sx={{ mt: 0.5, color: "error.main", fontSize: "0.75rem" }}>
                {error}
              </Box>
            )}
          </Box>
        );
      },
    },
    { type: "file", name: "banner", label: "Unit Banner" },
  ];

  const debouncedSearch = useDebouncedValue(search, 400);
  const dashboardStatus = Array.isArray(selectValue)
    ? String(selectValue[0] ?? "")
    : String(selectValue ?? "");

  // One page of appointments; branch, status, search and dates are filtered
  // on the server, oldest first like the table always showed them.
  const fetchAppointmentsPage = React.useCallback(
    async (params?: any) => {
      const page = Number(params?.page) || 0;
      const size = Number(params?.size) || DASHBOARD_PAGE_SIZE;
      const { start, end } = getDateRangeValues();
      const data = (await getDashboardAppointments({
        businessId: venueId,
        unitId: activeUnit || undefined,
        status: dashboardStatus || undefined,
        search: String(params?.search || "").trim() || undefined,
        startDate: start,
        endDate: end,
        page,
        size,
        sort: "startAt,asc",
      })) as any;
      const content = (data?.content ?? data?.data?.content ?? []) as any[];
      // Row actions look rows up by id on the page being shown.
      setRows(content);
      return {
        content,
        number: page,
        size,
        totalElements: Number(data?.totalElements ?? data?.data?.totalElements) || content.length,
        totalPages: Number(data?.totalPages ?? data?.data?.totalPages) || 1,
      };
    },
    // getDateRangeValues reads dateRange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeUnit, dashboardStatus, dateRange, venueId],
  );

  const exportCsv = React.useCallback(async (reportProgress?: ExportProgressReporter) => {
    const rows = await fetchAllDashboardRows(reportProgress);
    if (!rows || rows.length === 0) return;
    const keys = [
      "clientName",
      "phoneNumber",
      "date",
      "createdAt",
      "paid_at",
      "unit",
      "status",
      "startTime",
      "endTime",
      "invoiceTotal",
      "invoiceStatus",
      "promoCodeUsed",
      ...(SHOW.sportcietyColumn ? ["is_from_sportciety"] : []),
    ];
    const header = keys.join(",");
    const lines = rows.map((r: any) =>
      keys
        .map((k) => {
          const startLabel = convertUtcTimeToLocal(r.date, r.startTime);
          const endLabel = convertUtcTimeToLocal(r.date, r.endTime);
          const invoiceStatus = resolveDashboardInvoiceStatus(r);
          const createdAtFormatted = formatDashboardDate(
            r.createdAt || r.created_at,
          );
          const paidAtFormatted = formatDashboardDate(
            resolveDashboardPaidAt(r),
          );

          const normalized: Record<string, any> = {
            clientName: r.clientName ?? "",
            phoneNumber: r.phoneNumber ?? "",
            date: r.date ?? "",
            createdAt: createdAtFormatted === "-" ? "" : createdAtFormatted,
            paid_at: paidAtFormatted === "-" ? "" : paidAtFormatted,
            unit: r.unitName || r.unit?.name || r.unit || "",
            status: r.status ?? "",
            startTime: startLabel || "",
            endTime: endLabel || "",
            invoiceId: resolveDashboardInvoiceNumber(r),
            invoiceTotal: resolveDashboardInvoiceTotal(r),
            invoiceStatus,
            promoCodeUsed: r.promoCodeUsed ?? "-",
            is_from_sportciety: r.is_from_sportciety === true ? "Yes" : "No",
          };

          const s = String(normalized[k] ?? "").replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(","),
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    // fetchAllDashboardRows reads the current date range and venue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, venueId]);

  const [projectedRevenue, setProjectedRevenue] = React.useState<number | null>(
    null,
  );
  const [paidRevenue, setPaidRevenue] = React.useState<number | null>(null);
  const [paidExpenses, setPaidExpenses] = React.useState<number | null>(null);
  const [appointmentCount, setAppointmentCount] = React.useState<number | null>(
    null,
  );
  const [revenueLoading, setRevenueLoading] = React.useState(false);
  const [revenueRefreshKey, setRevenueRefreshKey] = React.useState(0);
  const [revenueVisible, setRevenueVisible] = React.useState(
    () => !SHOW.dashboardFinancePin || getCachedPinAccess(),
  );
  const [pinDialogOpen, setPinDialogOpen] = React.useState(false);
  const [services, setServices] = useState<Service[]>([]);

  React.useEffect(() => {
    const unsubscribe = subscribeToPinAccess((value) => {
      setRevenueVisible(!SHOW.dashboardFinancePin || value);
    });
    return unsubscribe;
  }, []);

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  const formatAmountString = (value: number | string | undefined) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric.toFixed(2);
    }
    return "0.00";
  };

  const formatDashboardMoney = (value: any): string => {
    if (value === undefined || value === null || value === "") return "";
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return numeric % 1 === 0 ? String(numeric) : numeric.toFixed(2);
  };

  const resolveDashboardInvoiceTotal = (row: any): string => {
    const candidates = [row?.invoiceTotal, row?.total_price];
    for (const candidate of candidates) {
      const formatted = formatDashboardMoney(candidate);
      if (formatted) return formatted;
    }
    return "";
  };

  const resolveDashboardInvoiceNumber = (row: any): string => {
    return (
      pickNonEmptyString(
        row?.invoice?.Invoice_number,
        row?.invoice?.invoice_number,
        row?.invoice?.invoiceNumber,
        row?.invoice?.invoice_nb,
        row?.Invoice_number,
        row?.invoice_number,
        row?.invoiceNumber,
        row?.invoice_nb,
        row?.invoiceId,
        row?.invoice_id,
        row?.invoice?.invoiceId,
        row?.invoice?.invoice_id,
        row?.invoice?.id,
      ) || ""
    );
  };

  const resolveDashboardPaidAt = (row: any): string => {
    const invoice = row?.invoice ?? {};
    const latestPayment = Array.isArray(row?.payments)
      ? row.payments[row.payments.length - 1]
      : Array.isArray(invoice?.payments)
      ? invoice.payments[invoice.payments.length - 1]
      : null;

    return (
      pickNonEmptyString(
        row?.paid_at,
        row?.paidAt,
        row?.payment_date,
        row?.paymentDate,
        invoice?.paid_at,
        invoice?.paidAt,
        invoice?.payment_date,
        invoice?.paymentDate,
        latestPayment?.paid_at,
        latestPayment?.paidAt,
        latestPayment?.created_at,
        latestPayment?.createdAt,
      ) || ""
    );
  };

  const formatDashboardDate = (value: any): string => {
    if (!value) return "-";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "-";
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const normalizeSearchText = (value: unknown): string => {
    return String(value ?? "").toLowerCase().trim();
  };

  const normalizeInvoiceNumberSearchText = (value: unknown): string => {
    return normalizeSearchText(value).replace(/[^a-z0-9]/g, "");
  };

  const normalizeDashboardInvoiceStatus = (value: unknown): string => {
    const raw = extractStatusString(value).trim();
    if (!raw) return "";

    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
      if (numeric === 1) return "PAID";
      if (numeric === 2) return "UNPAID";
      if (numeric === 3) return "CANCELLED";
      return "";
    }

    const normalized = raw.toUpperCase().replace(/[\s-]+/g, "_");
    if (normalized === "APPROVED") return "PAID";
    if (normalized === "VOID" || normalized === "VOIDED") return "CANCELLED";
    if (normalized === "REJECTED") return "CANCELLED";
    if (normalized === "PARTIALLY_PAID" || normalized === "PARTIAL_PAID") {
      return "PARTIALLY_PAID";
    }
    if (
      normalized === "PARTIALLY_REFUNDED" ||
      normalized === "PARTIAL_REFUNDED"
    ) {
      return "PARTIALLY_REFUNDED";
    }

    const allowed = new Set([
      "PENDING",
      "PAID",
      "PARTIALLY_PAID",
      "UNPAID",
      "REFUNDED",
      "PARTIALLY_REFUNDED",
      "CANCELLED",
    ]);

    return allowed.has(normalized) ? normalized : "";
  };

  const resolveDashboardInvoiceStatus = (row: any): string => {
    const candidates = [
      row?.invoice?.status,
      row?.invoice?.status_name,
      row?.invoice?.status_label,
      row?.invoice?.status_id,
      row?.invoice?.invoiceStatus,
      row?.invoice?.status?.name,
      row?.invoice?.status?.label,
      row?.invoice?.status?.code,
      row?.invoice?.status?.value,
      row?.invoice?.status?.id,
      row?.status_name,
      row?.status_label,
      row?.invoiceStatusId,
      row?.invoice_status_id,
      row?.invoiceStatus,
      row?.invoice_status,
    ];

    const normalizedCandidates = candidates
      .map(normalizeDashboardInvoiceStatus)
      .filter(Boolean);

    return (
      normalizedCandidates.find((status) => status !== "PENDING") ||
      normalizedCandidates[0] ||
      ""
    );
  };

  const fetchRevenueSummary = async () => {
    const { start, end } = getDateRangeValues();

    setRevenueLoading(true);

    try {
      const res = await getDashboardRevenueSummary({
        businessId: venueId,
        startDate: start,
        endDate: end,
      });

      setAppointmentCount(res.data.totalAppointments);
      setPaidRevenue(res.data.paidRevenue);
      setProjectedRevenue(res.data.projectedRevenue);
      setPaidExpenses(res.data.totalExpensesPaid);
    } catch (err) {
      console.error("Failed to fetch revenue summary", err);
      setAppointmentCount(null);
      setPaidRevenue(null);
      setPaidExpenses(null);
      setProjectedRevenue(null);
    } finally {
      setRevenueLoading(false);
    }
  };
  useEffect(() => {
    fetchRevenueSummary();
  }, [dateRange, venueId]);

  useEffect(() => {
    const handleLiveAppointmentUpdate = () => {
      void fetchDashboardData();
      void fetchRevenueSummary();
    };

    window.addEventListener(
      ADMIN_APPOINTMENT_SOCKET_EVENT,
      handleLiveAppointmentUpdate
    );

    return () => {
      window.removeEventListener(
        ADMIN_APPOINTMENT_SOCKET_EVENT,
        handleLiveAppointmentUpdate
      );
    };
  }, [dateRange, venueId]);

  const formatDateRangePart = (value?: string) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    if (trimmed.includes("T")) {
      return trimmed.split("T")[0];
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return "";
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const revenueRangeText = (() => {
    if (!dateRange) return "Date range: select in table";
    const startLabel = formatDateRangePart(dateRange.start);
    const endLabel = formatDateRangePart(dateRange.end);

    if (startLabel && endLabel) {
      return `Date range: ${startLabel} - ${endLabel}`;
    }
    if (startLabel) {
      return `Date range: ${startLabel}`;
    }
    if (endLabel) {
      return `Date range: ${endLabel}`;
    }
    return "Date range: select in table";
  })();

  const projectedRevenueDisplay = revenueLoading
    ? "Loading..."
    : projectedRevenue !== null
      ? formatAmountString(projectedRevenue)
      : "TBD";
  const paidRevenueDisplay = revenueLoading
    ? "Loading..."
    : paidRevenue !== null
      ? formatAmountString(paidRevenue)
      : "TBD";
  const paidExpensesDisplay = revenueLoading
    ? "Loading..."
    : paidExpenses !== null
      ? formatAmountString(paidExpenses)
      : "TBD";
  const appointmentCountDisplay = revenueLoading
    ? "Loading..."
    : appointmentCount !== null
      ? String(appointmentCount)
      : "TBD";
  const maskedProjectedRevenueDisplay = revenueVisible
    ? projectedRevenueDisplay
    : "Hidden";
  const maskedPaidRevenueDisplay = revenueVisible
    ? paidRevenueDisplay
    : "Hidden";
  const maskedPaidExpensesDisplay = revenueVisible
    ? paidExpensesDisplay
    : "Hidden";
  const maskedAppointmentCountDisplay = revenueVisible
    ? appointmentCountDisplay
    : "Hidden";

  // Clinic: calm white tiles with a tinted icon, two per row on phones.
  // Sports keeps its solid brand-colour tiles.
  const clinicSummaryCardSx = {
    borderRadius: 3.5,
    boxShadow: "none",
    minHeight: { xs: 112, sm: 132 },
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "center",
    bgcolor: "#FFFFFF",
    border: `1px solid ${BUSINESS_THEME.border}`,
    cursor: "pointer",
    transition: "transform 0.2s ease, border-color 0.2s ease",
    "& .MuiSvgIcon-root": {
      color: COLORS.MainColor,
      bgcolor: BUSINESS_THEME.tint,
      borderRadius: "10px",
      p: 0.75,
      fontSize: 36,
    },
    "&:hover": { transform: "translateY(-1px)", borderColor: COLORS.MainColor },
    "&:focus-visible": {
      outline: `3px solid ${COLORS.MainColor}55`,
      outlineOffset: 2,
    },
  };

  const summaryCardSx: SxProps<Theme> = IS_CLINIC ? clinicSummaryCardSx : {
    borderRadius: 4,
    boxShadow: 2,
    minHeight: { xs: 120, sm: 140 },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: COLORS.MainColor,
    border: "2px solid rgba(255, 255, 255, 0.2)",
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: 4,
    },
    "&:focus-visible": {
      outline: "2px solid rgba(255, 255, 255, 0.9)",
      outlineOffset: 2,
    },
  };

  const summaryCardContentSx: SxProps<Theme> = IS_CLINIC
    ? {
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: 0.5,
        p: { xs: 1.5, sm: 2 },
        "&:last-child": { pb: { xs: 1.5, sm: 2 } },
        "& .MuiTypography-root": { textAlign: "left", justifyContent: "flex-start" },
        // The value label centres itself for the sports tiles; keep it left here.
        "& > .MuiBox-root": { justifyContent: "flex-start" },
      }
    : {
        textAlign: "center",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
      };
  const summaryCardPrimaryTextColor = IS_CLINIC ? BUSINESS_THEME.text : COLORS.WhiteText;
  const summaryCardSecondaryTextColor = IS_CLINIC
    ? "#4B6066"
    : "rgba(255, 255, 255, 0.75)";
  const handleCardKeyDown = (
    event: React.KeyboardEvent,
    action: () => void,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };
  const navigateToAppointments = () => {
    navigate(`${basePath}/appointment`);
  };
  const navigateToInvoices = () => {
    if (!revenueVisible) {
      handleOpenPinDialog();
      toast.info("Unlock finance to view invoices.", { position: "top-right" });
      return;
    }
    if (pinDialogOpen) return;
    navigate(`${basePath}/finance/invoices`);
  };
  const navigateToExpenses = () => {
    if (!revenueVisible) {
      handleOpenPinDialog();
      toast.info("Unlock finance to view expenses.", { position: "top-right" });
      return;
    }
    if (pinDialogOpen) return;
    navigate(`${basePath}/finance/expenses`);
  };
  const handleOpenPinDialog = () => {
    if (revenueVisible || pinDialogOpen) return;
    setPinDialogOpen(true);
  };
  const handleClosePinDialog = () => {
    setPinDialogOpen(false);
  };
  const handlePinVerified = () => {
    setRevenueVisible(true);
    setPinDialogOpen(false);
  };
  const handleHideRevenue = () => {
    clearCachedPinAccess();
    setRevenueVisible(false);
    setPinDialogOpen(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        pt: 3,
        pb: 7,
      }}
    >
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* <Box
          sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" }, mb: 2 }}
        >
          Overview
        </Box> */}
        <Box
          sx={{
            display: "grid",
            gap: { xs: 2, sm: 3 },
            gridTemplateColumns: {
              xs: IS_CLINIC ? "repeat(2, 1fr)" : "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            alignItems: "stretch",
            justifyContent: "center",
          }}
        >
          <Card
            sx={summaryCardSx}
            onClick={navigateToAppointments}
            onKeyDown={(event) =>
              handleCardKeyDown(event, navigateToAppointments)
            }
            role="button"
            tabIndex={0}
            aria-label="Go to appointments"
          >
            <CardContent sx={summaryCardContentSx}>
              <EventAvailableIcon
                sx={{
                  fontSize: { xs: 32, sm: 30 },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text="Total Appointments"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.95rem", sm: "0.85rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CountUpLabel
                text={maskedAppointmentCountDisplay}
                fontWeight={700}
                center
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.4rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={revenueRangeText}
                fontWeight={500}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.7rem" },
                  color: summaryCardSecondaryTextColor,
                }}
              />
            </CardContent>
          </Card>

          <Card
            sx={summaryCardSx}
            onClick={navigateToInvoices}
            onKeyDown={(event) => handleCardKeyDown(event, navigateToInvoices)}
            role="button"
            tabIndex={0}
            aria-label="Go to invoices"
          >
            <CardContent sx={summaryCardContentSx}>
              <TrendingUpIcon
                sx={{
                  fontSize: { xs: 32, sm: 30 },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text="Projected Revenue"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.95rem", sm: "0.85rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CountUpLabel
                text={maskedProjectedRevenueDisplay}
                fontWeight={700}
                center
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.4rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={revenueRangeText}
                fontWeight={500}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.7rem" },
                  color: summaryCardSecondaryTextColor,
                }}
              />
            </CardContent>
          </Card>

          <Card
            sx={summaryCardSx}
            onClick={navigateToInvoices}
            onKeyDown={(event) => handleCardKeyDown(event, navigateToInvoices)}
            role="button"
            tabIndex={0}
            aria-label="Go to invoices"
          >
            <CardContent sx={summaryCardContentSx}>
              <PaidIcon
                sx={{
                  fontSize: { xs: 32, sm: 30 },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text="Paid Revenue"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.95rem", sm: "0.85rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CountUpLabel
                text={maskedPaidRevenueDisplay}
                fontWeight={700}
                center
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.4rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={revenueRangeText}
                fontWeight={500}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.7rem" },
                  color: summaryCardSecondaryTextColor,
                }}
              />
            </CardContent>
          </Card>
          <Card
            sx={summaryCardSx}
            onClick={navigateToExpenses}
            onKeyDown={(event) => handleCardKeyDown(event, navigateToExpenses)}
            role="button"
            tabIndex={0}
            aria-label="Go to expenses"
          >
            <CardContent sx={summaryCardContentSx}>
              <PaidIcon
                sx={{
                  fontSize: { xs: 32, sm: 30 },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text="Paid Expenses"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.95rem", sm: "0.85rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CountUpLabel
                text={maskedPaidExpensesDisplay}
                fontWeight={700}
                center
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.4rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={revenueRangeText}
                fontWeight={500}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.7rem" },
                  color: summaryCardSecondaryTextColor,
                }}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
      <Divider
        sx={{
          my: { xs: 2, sm: 3 },
          borderColor: COLORS.BorderColor,
          borderBottomWidth: 2,
        }}
      />
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box
            sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            {IS_CLINIC ? LABELS.units : "Branches"}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {SHOW.dashboardFinancePin && (
              <Button
                variant="outlined"
                onClick={revenueVisible ? handleHideRevenue : handleOpenPinDialog}
                disabled={pinDialogOpen}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {revenueVisible ? "Hide finance" : "Show finance"}
              </Button>
            )}
            {/* <CustomSelect
              label="Select Branch"
              value={selectedCourt}
              options={[
                { label: "All Branches", value: "all" },
                ...branches
                  .filter((b) => b && b.name)
                  .map((b) => ({
                    label: String(b.name),
                    value: String(b.id || b._id),
                  })),
              ]}
              onChange={(v: any) => setSelectedCourt(String(v || "all"))}
              sx={{ minWidth: 200 }}
            /> */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
              data-tour="add-unit"
            >
              {IS_CLINIC ? `Add ${LABELS.unit.toLowerCase()}` : "Add branch"}
            </Button>
          </Box>
        </Box>

        {unitStatsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: { xs: 2, sm: 3 },
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(6, 1fr)",
              },
              alignItems: "stretch",
              justifyContent: "center",
            }}
          >
            {(() => {
              const visibleUnits = unitList.filter((u) =>
                String(u || "").trim()
              );
              const unitCount = visibleUnits.length;
              const remainder = unitCount % 3;
              const lastRowStart = unitCount - (remainder || 3);

              return visibleUnits.map((unitName, index) => {
                const isLastRow = index >= lastRowStart;
                const spanMd =
                  remainder === 1 && isLastRow
                    ? 6
                    : remainder === 2 && isLastRow
                      ? 3
                      : 2;
                const unitDetails = units.find((u) => u.name === unitName);
                const unitIdKey = String(
                  unitDetails?.id || unitDetails?._id || "",
                );
                const totalClients = unitClientCounts[unitIdKey] ?? 0;
                const todaysAppointments = todayCountsByUnit[unitIdKey] ?? 0;
                const totalVenues = 1;

                return (
                  <Box
                    key={unitName}
                    sx={{
                      flex: 1,
                      cursor: "pointer",
                      gridColumn: { md: `span ${spanMd}` },
                    }}
                    onClick={() =>
                      setActiveUnit((prev) => {
                        const id = String(unitDetails?.id || unitDetails?._id);
                        return prev === id ? null : id;
                      })
                    }
                  >
                    <Card
                      sx={{
                        borderRadius: 4,
                        boxShadow:
                          activeUnit ===
                          String(unitDetails?.id || unitDetails?._id)
                            ? 6
                            : 2,
                        minHeight: { xs: 140, sm: 160 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: COLORS.WhiteBackground,
                        border: `2px solid ${
                          activeUnit ===
                          String(unitDetails?.id || unitDetails?._id)
                            ? COLORS.MainColor
                            : COLORS.BorderColor
                        }`,
                      }}
                    >
                      <CardContent sx={{ textAlign: "center", width: "100%" }}>
                        <CustomLabel
                          text={unitName}
                          fontWeight={550}
                          fontSize="1rem"
                          center
                          sx={{
                            mb: 2,
                            fontSize: { xs: "1.05rem", sm: "1rem" },
                            color: COLORS.BlackText,
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ textAlign: "center", minWidth: 80 }}>
                            <PeopleIcon
                              sx={{
                                fontSize: { xs: 28, sm: 26 },
                                color: COLORS.MainColor,
                                mb: 0.5,
                              }}
                            />
                            <CustomLabel
                              text={`Total ${LABELS.clients}`}
                              fontWeight={600}
                              sx={{
                                fontSize: { xs: "0.85rem", sm: "0.70rem" },
                              }}
                            />
                            <CustomLabel
                              text={String(totalClients)}
                              fontWeight={700}
                              center
                              sx={{
                                fontSize: { xs: "1rem", sm: "0.70rem" },
                                color: COLORS.BlackText,
                              }}
                            />
                          </Box>

                          <Box sx={{ textAlign: "center", minWidth: 90 }}>
                            <EditIcon
                              sx={{
                                fontSize: { xs: 28, sm: 26 },
                                color: COLORS.MainColor,
                                mb: 0.5,
                              }}
                            />
                            <CustomLabel
                              text="Today's Appointments"
                              fontWeight={600}
                              sx={{
                                fontSize: { xs: "0.85rem", sm: "0.70rem" },
                              }}
                            />
                            <CustomLabel
                              text={String(todaysAppointments)}
                              fontWeight={700}
                              center
                              sx={{
                                fontSize: { xs: "1rem", sm: "0.70rem" },
                                color: COLORS.BlackText,
                              }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                );
              });
            })()}
          </Box>
        )}
      </Box>
      <Divider
        sx={{
          my: { xs: 2, sm: 3 },
          borderColor: COLORS.BorderColor,
          borderBottomWidth: 2,
        }}
      />
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => {
              if (Array.isArray(v)) {
                const start = v[0];
                const end = v[1];
                if (start && end && new Date(start) > new Date(end)) {
                  setDateRange({ start: end, end: start });
                } else {
                  setDateRange({ start, end });
                }
              } else if (v && (v.start || v.end)) {
                const start = (v as any).start;
                const end = (v as any).end;
                if (start && end && new Date(start) > new Date(end)) {
                  setDateRange({ start: end, end: start });
                } else {
                  setDateRange({ start, end });
                }
              } else {
                setDateRange(null);
              }
            },

            fullWidth: false,
          }}
          selectConfig={{
            options: [
              { label: "Pending", value: "PENDING" },
              { label: "In Progress", value: "IN_PROGRESS" },
              { label: "Done", value: "DONE" },
              { label: "Cancelled", value: "CANCELLED" },
            ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select action",
            size: "medium",
            label: "Status ",
            searchable: false,
            clearable: true,

            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              label: "Export CSV",
              onClick: (reportProgress) => exportCsv(reportProgress),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{
            page: 0,
            size: DASHBOARD_PAGE_SIZE,
            unit: activeUnit,
            search: debouncedSearch,
            status: dashboardStatus,
            dateRange: JSON.stringify(dateRange ?? null),
          }}
          refreshSignal={appointmentsRefreshKey}
          paginationSummary={
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                fontWeight: 700,
              }}
            >
              <CustomLabel
                text="Total Revenue:"
                fontWeight={700}
                sx={{ color: COLORS.BlackText, fontSize: "0.875rem" }}
              />
              <CustomLabel
                text={paidRevenueDisplay}
                fontWeight={700}
                sx={{ color: COLORS.MainColor, fontSize: "0.875rem" }}
              />
            </Box>
          }
          fields={[
            {
              key: "invoiceId",
              header: "inv #",
              render: (row: any) => resolveDashboardInvoiceNumber(row) || "-",
            },
            { key: "clientName", header: "Client Name" },
            
            { key: "phoneNumber", header: "Phone " },
            // { key: "priority", header: "Priority" },
            { key: "date", header: "Date" },
            {
              key: "createdAt",
              header: "Created At",
              render: (row: any) => {
                return formatDashboardDate(row.createdAt || row.created_at);
              },
            },
            {
              key: "paid_at",
              header: "Paid At",
              render: (row: any) => {
                return formatDashboardDate(resolveDashboardPaidAt(row));
              },
            },
            {
              key: "unit",
              header: "Unit",
              render: (row: any) =>
                row.unitName || row.unit?.name || row.unit || "",
            },
            {
              key: "status",
              header: "Status",
              render: (row: any) => {
                const raw = row.status || "";
                const normalized = String(raw).toLowerCase();
                const label = normalized
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase());

                let bg = "#e0e0e0";
                let color = COLORS.BlackText;
                if (normalized === "cancelled") {
                  bg = COLORS.RED;
                  color = COLORS.WhiteText;
                } else if (normalized === "done") {
                  bg = "#4caf50";
                  color = COLORS.WhiteText;
                } else if (
                  normalized === "in_progress" ||
                  normalized === "in progress"
                ) {
                  bg = "#facc15";
                  color = COLORS.WhiteText;
                } else if (normalized === "pending") {
                  bg = COLORS.MainColor;
                  color = COLORS.WhiteText;
                }

                return (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 12,
                      backgroundColor: bg,
                      color,
                      fontWeight: 600,
                      minWidth: 70,
                      textAlign: "center",
                    }}
                  >
                    {label || "-"}
                  </span>
                );
              },
            },
            {
              key: "startTime",
              header: "Start Time",
              render: (row: any) =>
                convertUtcTimeToLocal(row.date, row.startTime),
            },
            {
              key: "endTime",
              header: "End Time",
              render: (row: any) =>
                convertUtcTimeToLocal(row.date, row.endTime),
            },
            {
              key: "servicePrice",
              header: "Service Price",
              render: (row: any) => {
                const raw = row?.servicePrice;
                if (raw === undefined || raw === null || raw === "") return "-";
                const text = String(raw);
                return text.trim().endsWith("$") ? text : `${text}$`;
              },
            },
            {
              key:'invoiceTotal',
              header:'Invoice Total',
              render: (row: any) => {
                const raw = resolveDashboardInvoiceTotal(row);
                if (!raw) return "-";
                const text = String(raw);
                return text.trim().endsWith("$") ? text : `${text}$`;
              },
            },
            {
              key: "invoiceStatus",
              header: "Invoice Status",
              render: (row: any) => {
                const normalized = resolveDashboardInvoiceStatus(row);

                // Match invoice status colors used in Invoices page
                let bg = COLORS.BorderColor;
                let color = COLORS.BlackText;
                const isPending = normalized === "PENDING";
                const isPaid = normalized === "PAID";
                const isCancelled =
                  normalized === "CANCELLED" || normalized === "REJECTED";

                if (isPending) {
                  bg = COLORS.MainColor;
                  color = COLORS.WhiteText;
                } else if (isPaid) {
                  bg = "#4caf50";
                  color = COLORS.WhiteText;
                } else if (isCancelled) {
                  bg = COLORS.RED;
                  color = COLORS.WhiteText;
                } else {
                  // other statuses (e.g., PARTIALLY_REFUNDED) use orange like Invoices page
                  bg = "#ff9800";
                  color = COLORS.WhiteText;
                }

                const label = normalized || "-";

                return (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 12,
                      backgroundColor: bg,
                      color,
                      fontWeight: 600,
                      minWidth: 90,
                      textAlign: "center",
                    }}
                  >
                    {label}
                  </span>
                );
              },
            },
            { key: "promoCodeUsed", header: "Promo" },
          ]}
          actions={wiredActions}
          fetchFunc={fetchAppointmentsPage}
        />        <GenericFormDialog
          open={invoiceEditOpen}
          onClose={handleInvoiceDialogClose}
          title={
            loadingInvoice
              ? "Loading invoice..."
              : invoiceDialogInvoiceNumber
                ? `Edit Invoice #${invoiceDialogInvoiceNumber}`
                : "Edit Invoice"
          }
          fields={invoiceFormFields}
          initialValues={invoiceDialogInitialValues}
          onSubmit={handleInvoiceSubmit}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={
            editingRow
              ? `Edit Appointment: ${
                  editingRow.name || editingRow.clientName || ""
                }${
                  appointmentDialogInvoiceNumber
                    ? ` | Invoice #${appointmentDialogInvoiceNumber}`
                    : ""
                }`
              : "Edit"
          }
          tabs={[{ label: "Appointment", fields: [...formFields] }]}
          initialValues={
            editingRow
              ? {
                  ...Object.keys(editingRow).reduce((acc: any, key) => {
                    const val = editingRow[key];
                    acc[key] =
                      val === undefined || val === null ? "" : String(val);
                    return acc;
                  }, {}),
                  unit_id:
                    editingRow.unit_id ||
                    editingRow.unit?.id ||
                    editingRow.unitId ||
                    units.find(
                      (b) =>
                        b.name === editingRow.unit?.name ||
                        b.name === editingRow.unit,
                    )?._id ||
                    units.find(
                      (b) =>
                        b.name === editingRow.unit?.name ||
                        b.name === editingRow.unit,
                    )?.id ||
                    "",
                  service_id:
                    editingRow.service_id || editingRow.serviceId || "",
                  time_start:
                    editingRow.date && editingRow.startTime
                      ? convertUtcTimeToLocal(
                          editingRow.date,
                          editingRow.startTime,
                        )
                      : "",

                  time_end:
                    editingRow.date && editingRow.endTime
                      ? convertUtcTimeToLocal(
                          editingRow.date,
                          editingRow.endTime,
                        )
                      : "",
                  status: editingRow.status
                    ? String(editingRow.status).toUpperCase()
                    : "",
                }
              : {}
          }
          onSubmit={async (values) => {
            if (!editingRow) return;
            try {
              // Ensure both servicePrice and serviceprice are sent to backend for compatibility
              if (values.servicePrice && !values.serviceprice) {
                values.serviceprice = values.servicePrice;
              }
              // Transform values to UpdateAppointmentDto format
              const updatePayload: any = {};

              if (values.service_id || values.serviceId) {
                updatePayload.service_id =
                  values.service_id || values.serviceId;
              }
              if (values.unit_id || values.unitId) {
                updatePayload.unit_id = values.unit_id || values.unitId;
              }
              if (values.notes) {
                updatePayload.notes = values.notes;
              }
              if (values.status) {
                updatePayload.status = String(values.status).toUpperCase();
              }
              const startDt =
                buildDateWithTime(
                  values.date || editingRow.date,
                  values.time_start
                ) || null;
              let endDt =
                buildDateWithTime(
                  values.date || editingRow.date,
                  values.time_end
                ) || null;

              if (!startDt || !endDt) {
                showErrorToast("Invalid start or end time.", {
                  position: "top-right",
                });
                return;
              }

              if (endDt.getTime() <= startDt.getTime()) {
                endDt = new Date(endDt.getTime());
                endDt.setDate(endDt.getDate() + 1);
              }

              updatePayload.start_at = startDt.toISOString();
              updatePayload.end_at = endDt.toISOString();

              await updateAppointment(editingRow.id, updatePayload);
              setRows((prev) =>
                prev.map((r: any) =>
                  String(r.id) === String(editingRow.id)
                    ? { ...r, ...values }
                    : r,
                ),
              );
              await fetchDashboardData();
              await fetchRevenueSummary();
              toast.success("Appointment updated successfully!", {
                position: "top-right",
              });
              setEditOpen(false);
              await fetchBranches();
            } catch (error: any) {
              console.error("Failed to update appointment:", error);
              const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to update appointment.";
              showErrorToast(message, {
                position: "top-right",
              });
            }
          }}
        />
        <GenericFormDialog
          open={dialogOpen}
          title={IS_CLINIC ? `Add new ${LABELS.unit.toLowerCase()}` : "Add New Unit"}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleDialogSubmit}
          fields={fields}
          initialValues={{
            countryCode: "+961",
          }}
        />

        <PinAccessDialog
          open={pinDialogOpen}
          onClose={handleClosePinDialog}
          onVerified={handlePinVerified}
          title="Show finance"
          description="Enter the PIN to show finance. Use Send PIN if you need it."
        />

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteAppointment(String(toDeleteId));
              setRows((prev) =>
                prev.filter((r: any) => String(r.id) !== String(toDeleteId)),
              );
              toast.success("Appointment deleted successfully!", {
                position: "top-right",
              });
              await fetchBranches();
            } catch (error) {
              console.error("Failed to delete appointment:", error);
              toast.error("Failed to delete appointment.", {
                position: "top-right",
              });
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
